import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShoppingBag,
  Shield,
  Check,
  AlertCircle,
  ArrowLeft,
  ArrowRightLeft,
  CreditCard,
  Luggage,
  Utensils,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FlightDetailsCard from "../components/FlightDetailsCard";
import BookingPageHeader from "../components/BookingPageHeader";
import InteractiveSeatMap from "../components/InteractiveSeatMap";
import { deriveDisplayFare, derivePaxCountsFromPassengers } from "../utils/deriveDisplayFare";
import { deriveCommissionFare, resolveCommissionPreview } from "../utils/deriveCommissionFare";
import FareCommissionRows from "../components/FareCommissionRows";
import { useAgentAuth } from "../context/AgentAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Temporary: set true for Tripjack ancillary segment console traces. */
const SSR_DEBUG = false;

/** Tripjack / Riya ancillary row identity: same SSR code can repeat on different segments (different `key`). */
function isSameSsrRow(sel, opt) {
  if (!sel || !opt) return false;
  return String(sel.code ?? '') === String(opt.code ?? '')
    && String(sel.key ?? '') === String(opt.key ?? '');
}

/** Strip UI-only leg prefixes (e.g. L0_, L1_) from seatmap tab keys — fallback when bookingSegmentId is missing. */
function normalizeTripjackSeatSegmentKey(segKey) {
  return String(segKey || '').replace(/^L\d+_/i, '');
}

function airportCode(node) {
  if (!node || typeof node !== 'object') return '';
  return String(node.code || node.airportCode || node.iata || node.ac || '').toUpperCase();
}

function pickTripInfosFromReview(reviewData) {
  if (!reviewData || typeof reviewData !== 'object') return null;
  const chain = [
    reviewData.tripInfos,
    reviewData.searchResult?.tripInfos,
    reviewData.data?.tripInfos,
  ];
  for (let i = 0; i < chain.length; i += 1) {
    const ti = chain[i];
    if (ti == null) continue;
    if (Array.isArray(ti) && ti.length === 0) continue;
    if (typeof ti === 'object' && !Array.isArray(ti) && Object.keys(ti).length === 0) continue;
    return ti;
  }
  return null;
}

/**
 * Split a single /review trip whose `sI` lists onward + return segments in order.
 * Conservative: requires the return leg to depart from the outbound "to" airport.
 * If unsafe, returns a single onward trip (no fake return).
 */
function trySplitRoundTripSingleTrip(trip, bookingData) {
  const si = trip?.sI;
  if (!trip || !Array.isArray(si) || si.length < 2) {
    return { onwardTrips: trip ? [trip] : [], returnTrips: [] };
  }
  const flight = bookingData?.flightData?.flight;
  const away = airportCode(flight?.to || flight?.aa || flight?.arrival);
  if (!away) {
    return { onwardTrips: [trip], returnTrips: [] };
  }

  let splitIdx = -1;
  for (let i = 1; i < si.length; i += 1) {
    if (airportCode(si[i]?.da) === away) {
      splitIdx = i;
      break;
    }
  }
  if (splitIdx < 1 || splitIdx >= si.length) {
    return { onwardTrips: [trip], returnTrips: [] };
  }

  const lastOnwardAa = airportCode(si[splitIdx - 1]?.aa);
  const firstReturnDa = airportCode(si[splitIdx]?.da);
  if (firstReturnDa !== away || lastOnwardAa !== away) {
    return { onwardTrips: [trip], returnTrips: [] };
  }

  return {
    onwardTrips: [{ ...trip, sI: si.slice(0, splitIdx) }],
    returnTrips: [{ ...trip, sI: si.slice(splitIdx) }],
  };
}

/**
 * Normalise Tripjack /review `tripInfos` (root array, dict ONWARD/RETURN/COMBO,
 * or nested under searchResult / data) into onward vs return trip-level objects.
 * Mirrors backend `collect_review_trip_legs` partitioning rules for the ancillary page.
 */
function normalizeTripjackTripInfos(reviewData, bookingData, isTripjackRT) {
  const empty = { onwardTrips: [], returnTrips: [] };
  const ti = pickTripInfosFromReview(reviewData);
  if (ti == null) return empty;

  if (Array.isArray(ti)) {
    const trips = ti.filter((t) => t && typeof t === 'object');
    if (trips.length === 0) return empty;

    if (!isTripjackRT) {
      return { onwardTrips: trips, returnTrips: [] };
    }
    if (trips.length >= 2) {
      return { onwardTrips: [trips[0]], returnTrips: [trips[1]] };
    }
    const split = trySplitRoundTripSingleTrip(trips[0], bookingData);
    if (split.returnTrips.length > 0) return split;
    return { onwardTrips: [trips[0]], returnTrips: [] };
  }

  if (typeof ti === 'object') {
    const onwardKeys = ['ONWARD', 'onward', 'O', 'ONE_WAY', 'DEPARTURE'];
    const returnKeys = ['RETURN', 'return', 'R'];
    const onwardTrips = [];
    const returnTrips = [];

    onwardKeys.forEach((k) => {
      const arr = ti[k];
      if (Array.isArray(arr)) {
        arr.forEach((t) => {
          if (t && typeof t === 'object') onwardTrips.push(t);
        });
      }
    });
    returnKeys.forEach((k) => {
      const arr = ti[k];
      if (Array.isArray(arr)) {
        arr.forEach((t) => {
          if (t && typeof t === 'object') returnTrips.push(t);
        });
      }
    });

    if (onwardTrips.length === 0 && returnTrips.length === 0 && Array.isArray(ti.COMBO)) {
      const combo = ti.COMBO.filter((x) => x && typeof x === 'object');
      if (isTripjackRT && combo.length >= 2) {
        return { onwardTrips: [combo[0]], returnTrips: [combo[1]] };
      }
      return { onwardTrips: combo, returnTrips: [] };
    }

    if (isTripjackRT && returnTrips.length === 0 && onwardTrips.length === 1) {
      const split = trySplitRoundTripSingleTrip(onwardTrips[0], bookingData);
      if (split.returnTrips.length > 0) return split;
    }

    return { onwardTrips, returnTrips };
  }

  return empty;
}

function mergeTripsToSsrOptions(trips, parseLegFromTripInfo) {
  const acc = { baggage: [], meals: [], seats: [], extras: [] };
  (trips || []).forEach((t) => {
    const p = parseLegFromTripInfo(t);
    acc.baggage.push(...p.baggage);
    acc.meals.push(...p.meals);
    acc.seats.push(...p.seats);
    acc.extras.push(...p.extras);
  });
  return acc;
}

/** Display-only: common supplier typo in meal descriptions. */
function formatSsrDisplayDesc(desc) {
  const s = String(desc ?? '').trim();
  if (!s) return s;
  if (/^ow\s+salt\s+meal/i.test(s)) return s.replace(/^ow\s+/i, 'Low ');
  if (/^ow\s*salt\b/i.test(s)) return s.replace(/^ow\s*/i, 'Low ');
  return s;
}

function tjStr(v) {
  return String(v == null ? '' : v).trim().toUpperCase();
}

/** Canonical Tripjack segment identity for itinerary rows and SSR rows (when route fields exist). */
function tripjackCanonicalSegmentKeyFromItinerarySi(s) {
  if (!s || typeof s !== 'object') return '';
  const alNode = s.al || s.airline;
  let airline = '';
  if (alNode && typeof alNode === 'object') {
    airline = tjStr(alNode.code || alNode.iata || alNode.airlineCode);
  } else if (typeof alNode === 'string') {
    airline = tjStr(alNode);
  }
  if (!airline) airline = tjStr(s.airlineCode || s.airline || s.validatingAirline || s.carrierCode);
  const fn = tjStr(
    (s.fN ?? s.flightNumber ?? s.flight_no ?? s.flightNo ?? s.fD?.fN ?? '').toString().replace(/\s+/g, '')
  );
  const fr = tjStr(
    airportCode(s.da) || s.from || s.departureAirport || s.origin || ''
  );
  const to = tjStr(
    airportCode(s.aa) || s.to || s.arrivalAirport || s.destination || ''
  );
  const rawDep = s.dt ?? s.dep_time ?? s.departure ?? s.departureTime ?? s.std ?? '';
  const dep = String(rawDep).trim().slice(0, 16);

  if (airline && fn && fr && to) {
    return dep ? `${airline}|${fn}|${fr}|${to}|${dep.toUpperCase()}` : `${airline}|${fn}|${fr}|${to}`;
  }
  /** Booking segment id (`sI.id`) — must win over supplier baggage *product* ids on `id`/`key`. */
  const bookingSeg = String(s.segmentId ?? s.bookingSegmentId ?? '').trim();
  if (bookingSeg) return `id|${tjStr(bookingSeg)}`;
  const id = String(s.id ?? s.key ?? '').trim();
  if (id) return `id|${tjStr(id)}`;
  return '';
}

/** Card title e.g. "AI 9718 • BOM → JAI". */
function tripjackSegmentCardLabelFromSi(s) {
  if (!s || typeof s !== 'object') return '';
  const alNode = s.al || s.airline;
  let al = '';
  if (alNode && typeof alNode === 'object') {
    al = String(alNode.code || alNode.iata || alNode.airlineCode || '').trim().toUpperCase();
  } else if (typeof alNode === 'string') {
    al = String(alNode).trim().toUpperCase();
  }
  if (!al) al = String(s.airlineCode || s.airline || '').trim().toUpperCase();
  const fnRaw = String(s.fN ?? s.flightNumber ?? s.flight_no ?? s.flightNo ?? s.fD?.fN ?? '').trim();
  const da = String(airportCode(s.da) || s.from || '').trim().toUpperCase();
  const aa = String(airportCode(s.aa) || s.to || '').trim().toUpperCase();
  const fnPart = (() => {
    if (!fnRaw) return '';
    const up = fnRaw.toUpperCase().replace(/\s+/g, '');
    if (al && up.startsWith(al)) return `${al} ${up.slice(al.length).replace(/^[-\s]*/, '') || up}`.trim();
    return fnRaw;
  })();
  const route = da && aa ? `${da} → ${aa}` : '';
  if (fnPart && route) return `${fnPart} • ${route}`;
  if (route) return route;
  if (fnPart) return fnPart;
  const id = String(s.id ?? s.key ?? '').trim();
  return id ? `Segment ${id}` : '';
}

/** ₹0 / missing ancillary amount → show “Included” instead of +₹0. */
function ssrAddonAmountIsIncluded(amount) {
  return (Number(amount) || 0) <= 0;
}

function dedupeSsrOptionsForSegment(arr, segmentKey, kind) {
  const seen = new Set();
  const out = [];
  (arr || []).forEach((o) => {
    const rawDesc = String(o?.desc ?? '').trim();
    const lab = formatSsrDisplayDesc(o?.desc);
    const pax = String(o?.passengerId ?? o?.pti ?? o?.paxId ?? '').trim();
    const pid = String(o?._tjBaggageProductId ?? o?._tjMealProductId ?? '').trim();
    const dk = `${segmentKey}|${pax}|${String(o?.code ?? '')}|${String(o?.key ?? '')}|${Number(o?.amount ?? 0)}|${rawDesc}|${lab}|${pid}`;
    if (seen.has(dk)) {
      if (SSR_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[SSR SEGMENT DEBUG]', 'skip_dup_option', { kind, dk });
      }
      return;
    }
    seen.add(dk);
    out.push(o);
  });
  return out;
}

/** Tripjack segment-scoped meal UI: { [segmentKey]: { [paxIdx]: mealOption } } */
function tripjackNestedMealsHasAnySelection(nested) {
  if (!nested || typeof nested !== 'object') return false;
  return Object.values(nested).some(
    (perPax) => perPax && typeof perPax === 'object'
      && Object.values(perPax).some((m) => m && m.key),
  );
}

function sumTripjackNestedMealSelections(nested) {
  let s = 0;
  Object.values(nested || {}).forEach((perPax) => {
    Object.values(perPax || {}).forEach((m) => {
      if (m && m.amount != null) s += Number(m.amount) || 0;
    });
  });
  return s;
}

/**
 * One ssrSelections.meals entry per segment meal (composite keys) so ReviewBooking
 * add-on totals and lists count every segment without changing passengers[].ssrMealInfos.
 */
function expandNestedTripjackMealsToSsrSelectionsMap(blocks, nested) {
  const out = {};
  let unk = 0;
  const usedSeg = new Set();
  (blocks || []).forEach((b) => {
    usedSeg.add(b.segmentKey);
    const perPax = nested?.[b.segmentKey];
    if (!perPax) return;
    Object.entries(perPax).forEach(([pIx, m]) => {
      if (!m?.key) return;
      const skEnc = encodeURIComponent(String(b.segmentKey)).replace(/%/g, '_');
      out[`tj:${pIx}:${skEnc}`] = m;
    });
  });
  Object.entries(nested || {}).forEach(([sk, perPax]) => {
    if (usedSeg.has(sk)) return;
    Object.entries(perPax || {}).forEach(([pIx, m]) => {
      if (!m?.key) return;
      unk += 1;
      out[`tj:${pIx}:x${unk}`] = m;
    });
  });
  return out;
}

/** Emit one { key, code } per segment meal for /book (segment order from blocks). */
function collectTripjackMealSsrRowsForPassenger(blocks, nested, paxIdx) {
  const rows = [];
  const usedSeg = new Set();
  (blocks || []).forEach((b) => {
    const m = nested?.[b.segmentKey]?.[paxIdx];
    if (m?.key) {
      rows.push({ key: m.key, code: m.code });
      usedSeg.add(b.segmentKey);
    }
  });
  Object.entries(nested || {}).forEach(([sk, perPax]) => {
    if (usedSeg.has(sk)) return;
    const m = perPax?.[paxIdx];
    if (m?.key) rows.push({ key: m.key, code: m.code });
  });
  return rows;
}

function mealSidebarRowsFromBlocksAndNested(blocks, nested, passengers) {
  const rows = [];
  (blocks || []).forEach((b) => {
    const perPax = nested?.[b.segmentKey];
    if (!perPax) return;
    Object.entries(perPax).forEach(([paxIdx, meal]) => {
      if (!meal?.key) return;
      const passenger = passengers[paxIdx] ?? passengers[Number(paxIdx)];
      rows.push({
        key: `${b.segmentKey}|${paxIdx}`,
        segmentLabel: b.label,
        passenger,
        meal,
      });
    });
  });
  return rows;
}

/** Same shape as meals — segment-scoped baggage picks for sidebar. */
function baggageSidebarRowsFromBlocksAndNested(blocks, nested, passengers) {
  const rows = [];
  (blocks || []).forEach((b) => {
    const perPax = nested?.[b.segmentKey];
    if (!perPax) return;
    Object.entries(perPax).forEach(([paxIdx, baggage]) => {
      if (!baggage?.key) return;
      const passenger = passengers[paxIdx] ?? passengers[Number(paxIdx)];
      rows.push({
        key: `${b.segmentKey}|${paxIdx}`,
        segmentLabel: b.label,
        passenger,
        baggage,
      });
    });
  });
  return rows;
}

/** Tripjack segment-scoped baggage UI: { [segmentKey]: { [paxIdx]: option } } */
function tripjackNestedBaggageHasAnySelection(nested) {
  if (!nested || typeof nested !== 'object') return false;
  return Object.values(nested).some(
    (perPax) => perPax && typeof perPax === 'object'
      && Object.values(perPax).some((b) => b && b.key),
  );
}

function sumTripjackNestedBaggageSelections(nested) {
  let s = 0;
  Object.values(nested || {}).forEach((perPax) => {
    Object.values(perPax || {}).forEach((b) => {
      if (b && b.amount != null) s += Number(b.amount) || 0;
    });
  });
  return s;
}

/**
 * One ssrSelections.baggage entry per segment pick (composite keys) for review totals / list.
 * Prefix `tjB:` avoids collision with meal keys in a different map.
 */
function expandNestedTripjackBaggageToSsrSelectionsMap(blocks, nested) {
  const out = {};
  let unk = 0;
  const usedSeg = new Set();
  (blocks || []).forEach((b) => {
    usedSeg.add(b.segmentKey);
    const perPax = nested?.[b.segmentKey];
    if (!perPax) return;
    Object.entries(perPax).forEach(([pIx, bg]) => {
      if (!bg?.key) return;
      const skEnc = encodeURIComponent(String(b.segmentKey)).replace(/%/g, '_');
      out[`tjB:${pIx}:${skEnc}`] = bg;
    });
  });
  Object.entries(nested || {}).forEach(([sk, perPax]) => {
    if (usedSeg.has(sk)) return;
    Object.entries(perPax || {}).forEach(([pIx, bg]) => {
      if (!bg?.key) return;
      unk += 1;
      out[`tjB:${pIx}:x${unk}`] = bg;
    });
  });
  return out;
}

function pickSegmentsFromLegFlightDetails(legOrFlight) {
  if (!legOrFlight) return [];
  if (Array.isArray(legOrFlight.segments) && legOrFlight.segments.length > 0) {
    return legOrFlight.segments.filter(Boolean);
  }
  if (Array.isArray(legOrFlight.sI) && legOrFlight.sI.length > 0) {
    return legOrFlight.sI.filter(Boolean);
  }
  if (legOrFlight.da || legOrFlight.aa || legOrFlight.fD || legOrFlight.fN) {
    return [legOrFlight];
  }
  return [];
}

/** Mirrors `FlightDetailsCard.detectShape` + `pickSegments` for ancillary segment lists. */
function detectFlightDataShapeForSegments(fd) {
  if (!fd || typeof fd !== 'object') return { kind: 'empty' };
  if (fd.onward && fd.return) {
    return { kind: 'rt', onward: fd.onward, ret: fd.return };
  }
  if (fd.flight?.onwardSegments || fd.flight?.returnSegments) {
    return {
      kind: 'rt',
      onward: { segments: fd.flight.onwardSegments || [] },
      ret: { segments: fd.flight.returnSegments || [] },
    };
  }
  if (fd.flight && (fd.flight.sI?.length || fd.flight.segments?.length)) {
    return { kind: 'leg', leg: fd.flight };
  }
  return { kind: 'leg', leg: fd };
}

/**
 * Single source for itinerary rows: same shapes as `FlightDetailsCard`, then
 * `/review` tripInfos, then seatmap `segmentsForUI` (tab order) as fallback.
 */
function extractVisibleFlightSegments({
  flightData,
  bookingData,
  reviewData,
  seatmapEnrichedSegments,
}) {
  const fd = flightData ?? bookingData?.flightData;
  const out = [];

  const pushLeg = (leg) => {
    pickSegmentsFromLegFlightDetails(leg).forEach((s) => {
      if (s) out.push(s);
    });
  };

  const shape = detectFlightDataShapeForSegments(fd);
  if (shape.kind === 'rt') {
    pushLeg(shape.onward);
    pushLeg(shape.ret);
  } else if (shape.kind === 'leg') {
    pushLeg(shape.leg);
  }

  if (out.length === 0 && fd) {
    if (Array.isArray(fd.segments) && fd.segments.length > 0) {
      fd.segments.forEach((s) => {
        if (s) out.push(s);
      });
    }
    if (out.length === 0 && Array.isArray(fd.tripInfos)) {
      fd.tripInfos.forEach((t) => {
        (t?.sI || []).forEach((s) => {
          if (s) out.push(s);
        });
      });
    }
  }

  if (out.length === 0 && reviewData) {
    flattenAllTripLegsFromReviewTripInfos(reviewData).forEach((trip) => {
      (trip.sI || []).forEach((s) => {
        if (s) out.push(s);
      });
    });
  }

  if (out.length === 0 && Array.isArray(seatmapEnrichedSegments) && seatmapEnrichedSegments.length > 0) {
    seatmapEnrichedSegments.forEach((seg) => {
      const tabKey = String(seg.key ?? '').trim();
      const rawId = tabKey.replace(/^L\d+_/i, '') || tabKey;
      out.push({
        id: rawId,
        key: rawId,
        da: { code: seg.from },
        aa: { code: seg.to },
        fN: seg.flight_no,
        flightNumber: seg.flight_no,
        dt: seg.dt || seg.dep_time,
        _source: 'seatmap',
        _seatmapKey: tabKey,
      });
    });
  }

  return out;
}

const OTHER_SEGMENT_ID = '__tj_other__';

function buildMatchKeySetForSegment(segmentId, seatmapTabKey) {
  const m = new Set();
  const sid = String(segmentId || '').trim();
  if (sid) {
    m.add(sid);
    m.add(tjStr(sid));
    const st = sid.replace(/^L\d+_/i, '');
    if (st && st !== sid) m.add(st);
  }
  const sk = String(seatmapTabKey || '').trim();
  if (sk) {
    m.add(sk);
    m.add(tjStr(sk));
    const st2 = sk.replace(/^L\d+_/i, '');
    if (st2 && st2 !== sk) m.add(st2);
  }
  return m;
}

function tripjackSegmentFlightRouteSignature(ref) {
  if (!ref || typeof ref !== 'object') return { fn: '', fr: '', to: '' };
  const fn = tjStr(
    (ref.fN ?? ref.flightNumber ?? ref.flight_no ?? ref.flightNo ?? ref.fD?.fN ?? '')
      .toString()
      .replace(/\s+/g, ''),
  );
  const fr = tjStr(airportCode(ref.da) || ref.from || ref.origin || ref.departureAirport || '');
  const to = tjStr(airportCode(ref.aa) || ref.to || ref.destination || ref.arrivalAirport || '');
  return { fn, fr, to };
}

function tripjackSsrRowRouteSignature(o) {
  if (!o || typeof o !== 'object') return { fn: '', fr: '', to: '' };
  const fn = tjStr(
    (o.flightNumber ?? o.flight_no ?? o.flightNo ?? o.fN ?? o.fD?.fN ?? '')
      .toString()
      .replace(/\s+/g, ''),
  );
  const fr = tjStr(
    airportCode(o.da) || o.from || o.origin || o.departureAirport || o.src || o.dep || '',
  );
  const to = tjStr(
    airportCode(o.aa) || o.to || o.destination || o.arrivalAirport || o.dest || o.arr || '',
  );
  return { fn, fr, to };
}

function tripjackSsrRowHasSegmentLinkage(o) {
  if (!o || typeof o !== 'object') return false;
  if (String(o.segmentId ?? o.bookingSegmentId ?? '').trim()) return true;
  const r = tripjackSsrRowRouteSignature(o);
  return !!(r.fn && r.fr && r.to);
}

/**
 * Product-level fingerprint for comparing baggage catalogue rows across segments.
 * Omits supplier segment booking `key` so duplicated per-segment copies still match as one set.
 */
function baggageJourneyEquivalenceSignature(o) {
  if (!o || typeof o !== 'object') return '';
  const pid = String(o?._tjBaggageProductId ?? '').trim();
  const code = String(o?.code ?? '').trim();
  const amt = Number(o?.amount ?? 0) || 0;
  const lab = formatSsrDisplayDesc(o?.desc);
  const ro = tripjackSsrRowRouteSignature(o);
  const rt = ro.fn && ro.fr && ro.to ? `${ro.fn}|${ro.fr}|${ro.to}` : '';
  const wt = String(o?.weight ?? o?.baggageWeight ?? o?.baggageWt ?? '').trim();
  return `${pid}|${code}|${amt}|${lab}|${rt}|${wt}`;
}

/** True if two baggage option lists are the same multiset (order ignored). */
function areBaggageSetsEquivalent(a, b) {
  const sigs = (arr) => {
    const m = new Map();
    (arr || []).forEach((o) => {
      const k = baggageJourneyEquivalenceSignature(o);
      m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
  };
  const ma = sigs(a);
  const mb = sigs(b);
  if (ma.size !== mb.size) return false;
  for (const [k, v] of ma) {
    if (mb.get(k) !== v) return false;
  }
  return true;
}

/**
 * Product-identity signature — IGNORES `amount`. Used to detect the
 * industry-standard "connecting baggage" supplier pattern where a 15 KG
 * bag is priced ₹1500 on segment-1 and ₹0 "included" on segment-2 (same
 * product, billed once across the leg). When this signature is equivalent
 * across all segments, we collapse to a leg-global selector and let the
 * per-segment payload builder reconstitute one ssrBaggageInfos per seg
 * using each segment's NATIVE price row.
 */
function baggageProductIdentitySignature(o) {
  if (!o || typeof o !== 'object') return '';
  const pid = String(o?._tjBaggageProductId ?? '').trim();
  const code = String(o?.code ?? '').trim();
  const lab = formatSsrDisplayDesc(o?.desc);
  const wt = String(o?.weight ?? o?.baggageWeight ?? o?.baggageWt ?? '').trim();
  return `${pid}|${code}|${lab}|${wt}`;
}

/** True if every segment's baggage list contains the SAME product codes
 *  (price is intentionally ignored). Catches the ₹1500 + ₹0 connecting pattern. */
function areBaggageProductSetsEquivalent(a, b) {
  const sigs = (arr) => {
    const s = new Set();
    (arr || []).forEach((o) => {
      const k = baggageProductIdentitySignature(o);
      if (k) s.add(k);
    });
    return s;
  };
  const sa = sigs(a);
  const sb = sigs(b);
  if (sa.size === 0 || sa.size !== sb.size) return false;
  for (const k of sa) {
    if (!sb.has(k)) return false;
  }
  return true;
}

/**
 * Strong segment linkage (for mode heuristics): segment id, row key vs block,
 * canonical segment key, flight+route, or dep date match to an itinerary block.
 */
function tripjackBaggageRowHasStrongSegmentLinkage(row, blocks) {
  if (!row || !Array.isArray(blocks) || blocks.length === 0) return false;
  const bookSeg = String(row.segmentId ?? row.bookingSegmentId ?? '').trim();
  if (bookSeg) {
    for (let i = 0; i < blocks.length; i += 1) {
      if (blocks[i].matchIds.has(bookSeg) || blocks[i].segmentId === bookSeg) return true;
    }
  }
  const rowKey = String(row.key ?? '').trim();
  if (rowKey) {
    for (const b of blocks) {
      if (rowKey === b.segmentKey || rowKey === b.segmentId || b.matchIds.has(rowKey)) return true;
    }
  }
  const rowCanon = tripjackCanonicalSegmentKeyFromItinerarySi(row);
  if (rowCanon && blocks.some((b) => b.segmentKey === rowCanon)) return true;
  const ro = tripjackSsrRowRouteSignature(row);
  if (ro.fn && ro.fr && ro.to) {
    const hit = blocks.some((b) => {
      const br = tripjackSegmentFlightRouteSignature(b.segmentRef);
      return ro.fn === br.fn && ro.fr === br.fr && ro.to === br.to;
    });
    if (hit) return true;
  }
  const dt = String(row.dt ?? row.departureDateTime ?? row.departureTime ?? '').trim();
  if (dt) {
    const d10 = dt.slice(0, 10);
    const hitDt = blocks.some((b) => {
      const s = b.segmentRef;
      const sdt = String(s?.dt ?? s?.departureTime ?? '').trim();
      return sdt && sdt.slice(0, 10) === d10;
    });
    if (hitDt) return true;
  }
  return false;
}

/**
 * "segment" = per-sector baggage cards; "journey" = one journey card (identical catalogue per block or no usable linkage).
 */
function detectTripjackBaggageMode({ baggageOptions, segmentBlocks }) {
  const blocks = Array.isArray(segmentBlocks) ? segmentBlocks : [];
  if (blocks.length <= 1) return 'segment';

  const blockBags = blocks.map((b) => b.baggageOptions || []);
  const allNonEmpty = blockBags.every((x) => x.length > 0);
  if (!allNonEmpty) return 'segment';

  const flat = Array.isArray(baggageOptions) ? baggageOptions : [];
  const weakLinkage = flat.length > 0
    && flat.every((r) => !tripjackBaggageRowHasStrongSegmentLinkage(r, blocks));
  if (weakLinkage) return 'journey';

  const allEquiv = blockBags.every((bag) => areBaggageSetsEquivalent(bag, blockBags[0]));
  if (allEquiv) return 'journey';

  // Industry-standard "connecting baggage" pattern: supplier ships the
  // same product code on every segment but with different prices (often
  // ₹X on segment-1, ₹0 "included" on segment-2). Treat as leg-global —
  // the per-segment payload builder will still emit one ssrBaggageInfos
  // entry per segment using each segment's native row, so the supplier
  // contract is preserved AND the user sees one accurate price.
  const allProductEquiv = blockBags.every(
    (bag) => areBaggageProductSetsEquivalent(bag, blockBags[0]),
  );
  if (allProductEquiv) return 'journey';

  return 'segment';
}

/**
 * Returns a deduped list of leg-baggage options where each row carries:
 *   - the supplier's native first-seen `code`/`desc`/`key` (for payload),
 *   - `_legTotalAmount`: SUM of per-segment amounts for this product code
 *     across every segment block of the leg (what the user actually pays
 *     when this option is selected),
 *   - `_legSegmentPriceMap`: { segmentKey: row } so the picker can fan
 *     out the choice across segments without losing per-seg pricing.
 */
function buildLegBaggageDisplayOptions(blocks) {
  const blockList = Array.isArray(blocks) ? blocks : [];
  if (!blockList.length) return [];
  const byProduct = new Map();
  blockList.forEach((b) => {
    (b.baggageOptions || []).forEach((row) => {
      const sig = baggageProductIdentitySignature(row);
      if (!sig) return;
      const amt = Number(row?.amount ?? 0) || 0;
      if (!byProduct.has(sig)) {
        byProduct.set(sig, {
          sig,
          firstRow: row,
          legTotal: amt,
          perSegment: { [b.segmentKey]: row },
        });
      } else {
        const entry = byProduct.get(sig);
        entry.legTotal += amt;
        // Track each segment's native priced row so the picker can write them
        // back into `tripjackSegmentedBaggageSelections`.
        if (!entry.perSegment[b.segmentKey]) {
          entry.perSegment[b.segmentKey] = row;
        }
      }
    });
  });
  // Sort by leg-total ASC so cheapest first (matches single-segment UX).
  const arr = Array.from(byProduct.values()).sort((a, b) => a.legTotal - b.legTotal);
  return arr.map((e) => ({
    ...e.firstRow,
    _legTotalAmount: Math.round(e.legTotal * 100) / 100,
    _legSegmentPriceMap: e.perSegment,
  }));
}

/** One representative row per journey-equivalent product (first occurrence wins — real API row for /book). */
function dedupeBaggageOptionsForJourneyDisplay(flatOptions) {
  const seen = new Set();
  const out = [];
  (flatOptions || []).forEach((o) => {
    const k = baggageJourneyEquivalenceSignature(o);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(o);
  });
  return out;
}

/** e.g. "BOM → HYD via DEL" for multi-sector journey baggage title. */
function formatTripjackJourneyBaggageRouteLabel(segments) {
  const segs = Array.isArray(segments) ? segments.filter(Boolean) : [];
  if (!segs.length) return '';
  const first = segs[0];
  const last = segs[segs.length - 1];
  const fr = airportCode(first?.da || first?.from) || String(first?.from || '').trim().toUpperCase();
  const to = airportCode(last?.aa || last?.to) || String(last?.to || '').trim().toUpperCase();
  if (!fr || !to) return '';
  if (segs.length <= 2) return `${fr} → ${to}`;
  const hops = [];
  for (let i = 0; i < segs.length - 1; i += 1) {
    const aa = airportCode(segs[i]?.aa);
    if (aa) hops.push(aa);
  }
  const vias = [...new Set(hops)].filter((v) => v && v !== fr && v !== to);
  if (!vias.length) return `${fr} → ${to}`;
  return `${fr} → ${to} via ${vias.join(', ')}`;
}

/** Last resort: spread catalogue rows across itinerary blocks (stamps /book `key` per block). */
function distributeUnmatchedTripjackSsrRows(blocks, rows, kind) {
  if (!rows.length || !blocks.length) return;
  const sorted = [...rows].sort((a, b) => {
    const pid = (x) => String(x._tjBaggageProductId ?? x._tjMealProductId ?? '');
    const ka = `${String(a.code)}|${Number(a.amount)}|${String(a.desc)}|${pid(a)}`;
    const kb = `${String(b.code)}|${Number(b.amount)}|${String(b.desc)}|${pid(b)}`;
    return ka.localeCompare(kb);
  });
  const n = blocks.length;
  sorted.forEach((row, i) => {
    const block = blocks[i % n];
    const bookingKey = String(block.segmentRef?.id ?? block.segmentId ?? '').trim();
    const stamped = bookingKey
      ? {
        ...row,
        key: bookingKey,
        segmentId: bookingKey,
        bookingSegmentId: bookingKey,
      }
      : row;
    if (kind === 'bag') block.baggageOptions.push(stamped);
    else block.mealOptions.push(stamped);
  });
  if (SSR_DEBUG) {
    const anyMeta = rows.some((r) => tripjackSsrRowHasSegmentLinkage(r));
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG]', 'distributed_fallback_round_robin', {
      kind,
      rowCount: rows.length,
      blockCount: n,
      rowsHadSomeLinkageMetadata: anyMeta,
    });
  }
}

/**
 * Audit §6.1 (May-2026) — Collapse "no-linkage" SSR rows onto the first
 * segment block + mark the block so the UI can show a "applies to the whole
 * journey" banner. Previously these rows went through
 * `distributeUnmatchedTripjackSsrRows()` which round-robin'd them across all
 * segments, giving users a false impression of segment-specific options
 * (Issue 5 in the audit). The supplier returned a single global option per
 * code — collapsing to one block correctly reflects that.
 */
function collapseUnlinkedSsrRowsToFirstBlock(blocks, rows, kind) {
  if (!rows.length || !blocks.length) return;
  const target = blocks[0];
  const bookingKey = String(target.segmentRef?.id ?? target.segmentId ?? '').trim();
  rows.forEach((row) => {
    const stamped = bookingKey
      ? {
        ...row,
        key: bookingKey,
        segmentId: bookingKey,
        bookingSegmentId: bookingKey,
      }
      : row;
    if (kind === 'bag') {
      target.baggageOptions.push(stamped);
      target._wholeJourneyBag = true;
    } else {
      target.mealOptions.push(stamped);
      target._wholeJourneyMeal = true;
    }
  });
  if (SSR_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG]', 'collapsed_unlinked_to_first_block', {
      kind,
      rowCount: rows.length,
      targetSegmentKey: target.segmentKey,
    });
  }
}

function matchTripjackSsrRowToBlockIndex(o, blocks) {
  const bookSeg = String(o.segmentId ?? o.bookingSegmentId ?? '').trim();
  if (bookSeg) {
    for (let i = 0; i < blocks.length; i += 1) {
      if (blocks[i].matchIds.has(bookSeg) || blocks[i].segmentId === bookSeg) return i;
    }
  }
  const rowCanon = tripjackCanonicalSegmentKeyFromItinerarySi(o);
  if (rowCanon && !rowCanon.startsWith('id|')) {
    const ix = blocks.findIndex((b) => b.segmentKey === rowCanon);
    if (ix >= 0) return ix;
  }
  if (rowCanon && rowCanon.startsWith('id|')) {
    const rid = rowCanon.slice(3).trim();
    if (rid) {
      for (let i = 0; i < blocks.length; i += 1) {
        if (blocks[i].matchIds.has(rid) || blocks[i].segmentId === rid) return i;
      }
    }
  }
  const ro = tripjackSsrRowRouteSignature(o);
  if (ro.fn && ro.fr && ro.to) {
    return blocks.findIndex((b) => {
      const br = tripjackSegmentFlightRouteSignature(b.segmentRef);
      return ro.fn === br.fn && ro.fr === br.fr && ro.to === br.to;
    });
  }
  return -1;
}

/**
 * Ordered segment blocks. Match SSR rows by booking segment id, canonical route key, or route/flight.
 * Do not treat baggage *product* `id`/`key` as segment ids — those are stamped separately on parse.
 */
function buildTripjackSegmentAncillaryBlocks(baggageOpts, mealOpts, itineraryRows) {
  const rows = Array.isArray(itineraryRows) ? itineraryRows.filter(Boolean) : [];
  if (rows.length === 0) {
    if (SSR_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSR SEGMENT DEBUG]', 'no_itinerary_rows', { baggage: (baggageOpts || []).length, meals: (mealOpts || []).length });
    }
    return [];
  }

  const blocks = rows.map((s, rowIdx) => {
    const canon = tripjackCanonicalSegmentKeyFromItinerarySi(s) || '';
    let segmentId = String(s?.id ?? s?.key ?? s.segmentId ?? '').trim();
    if (!segmentId && canon.startsWith('id|')) segmentId = canon.slice(3).trim();
    if (!segmentId && s?._seatmapKey) segmentId = String(s._seatmapKey).trim();
    if (!segmentId && canon && !canon.startsWith('id|')) segmentId = canon;
    if (!segmentId) segmentId = `seg-${rowIdx}`;
    const segmentKey = canon || `id|${tjStr(segmentId)}`;
    const seatmapTabKey = s._seatmapKey || '';
    const matchIds = buildMatchKeySetForSegment(segmentId, seatmapTabKey);
    // Industry-standard leg tagging — preserves multi-city support.
    // `legIndex` comes from `itineraryRows` upstream (each row is tagged
    // `leg: 'onward'|'return'` and `_legIndex` when the booking page
    // constructed the row). Fallback: 0.
    const legIndex = Number.isFinite(Number(s?._legIndex))
      ? Number(s._legIndex)
      : (s?.leg === 'return' ? 1 : 0);
    return {
      segmentKey,
      segmentId,
      label: tripjackSegmentCardLabelFromSi(s) || `Segment ${segmentId}`,
      segmentRef: s,
      matchIds,
      baggageOptions: [],
      mealOptions: [],
      legIndex,
    };
  });

  if (SSR_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG]', 'itinerary_canonical_keys', blocks.map((b) => ({
      segmentId: b.segmentId,
      segmentKey: b.segmentKey,
      label: b.label,
    })));
  }

  const unmatchedBag = [];
  const unmatchedMeal = [];

  const collectOrMatch = (o, kind) => {
    const idx = matchTripjackSsrRowToBlockIndex(o, blocks);
    if (idx >= 0) {
      if (kind === 'bag') blocks[idx].baggageOptions.push(o);
      else blocks[idx].mealOptions.push(o);
      return;
    }
    if (SSR_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSR SEGMENT DEBUG]', 'ssr_row_unmatched', {
        kind,
        bookingSeg: String(o.segmentId ?? o.bookingSegmentId ?? '') || null,
        rowCanon: tripjackCanonicalSegmentKeyFromItinerarySi(o) || null,
        route: tripjackSsrRowRouteSignature(o),
        code: o?.code,
      });
    }
    if (kind === 'bag') unmatchedBag.push(o);
    else unmatchedMeal.push(o);
  };

  (baggageOpts || []).forEach((o) => {
    if (SSR_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSR SEGMENT DEBUG]', 'ssr_baggage_row', {
        bookingSegmentId: String(o?.bookingSegmentId ?? o?.segmentId ?? ''),
        rowCanon: tripjackCanonicalSegmentKeyFromItinerarySi(o),
        code: o?.code,
      });
    }
    collectOrMatch(o, 'bag');
  });
  (mealOpts || []).forEach((o) => {
    if (SSR_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSR SEGMENT DEBUG]', 'ssr_meal_row', {
        bookingSegmentId: String(o?.bookingSegmentId ?? o?.segmentId ?? ''),
        rowCanon: tripjackCanonicalSegmentKeyFromItinerarySi(o),
        code: o?.code,
      });
    }
    collectOrMatch(o, 'meal');
  });

  const noLinkageBag = unmatchedBag.filter((r) => !tripjackSsrRowHasSegmentLinkage(r));
  const noLinkageMeal = unmatchedMeal.filter((r) => !tripjackSsrRowHasSegmentLinkage(r));
  const looseBag = unmatchedBag.filter((r) => tripjackSsrRowHasSegmentLinkage(r));
  const looseMeal = unmatchedMeal.filter((r) => tripjackSsrRowHasSegmentLinkage(r));

  if (SSR_DEBUG && (unmatchedBag.length || unmatchedMeal.length)) {
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG]', 'unmatched_counts', {
      baggage: { total: unmatchedBag.length, noLinkage: noLinkageBag.length, withLinkage: looseBag.length },
      meals: { total: unmatchedMeal.length, noLinkage: noLinkageMeal.length, withLinkage: looseMeal.length },
    });
  }

  if (noLinkageBag.length) collapseUnlinkedSsrRowsToFirstBlock(blocks, noLinkageBag, 'bag');
  if (looseBag.length) {
    if (SSR_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSR SEGMENT DEBUG]', 'distribute_baggage_had_linkage_but_no_block_match', { count: looseBag.length });
    }
    distributeUnmatchedTripjackSsrRows(blocks, looseBag, 'bag');
  }

  if (noLinkageMeal.length) collapseUnlinkedSsrRowsToFirstBlock(blocks, noLinkageMeal, 'meal');
  if (looseMeal.length) {
    if (SSR_DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[SSR SEGMENT DEBUG]', 'distribute_meals_had_linkage_but_no_block_match', { count: looseMeal.length });
    }
    distributeUnmatchedTripjackSsrRows(blocks, looseMeal, 'meal');
  }

  blocks.forEach((b) => {
    b.baggageOptions = dedupeSsrOptionsForSegment(b.baggageOptions, b.segmentKey, 'baggage');
    b.mealOptions = dedupeSsrOptionsForSegment(b.mealOptions, b.segmentKey, 'meal');
  });

  if (SSR_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG]', 'final_segment_blocks', blocks.map((b) => ({
      segmentId: b.segmentId,
      segmentKey: b.segmentKey,
      label: b.label,
      baggageCount: b.baggageOptions.length,
      mealCount: b.mealOptions.length,
    })));
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG] MATCHED_BLOCKS', blocks.map((b) => ({
      label: b.label,
      segmentId: b.segmentId,
      baggageCount: b.baggageOptions.length,
      mealCount: b.mealOptions.length,
      sampleBaggageLabels: (b.baggageOptions || []).slice(0, 6).map((x) => formatSsrDisplayDesc(x?.desc)),
      sampleMealLabels: (b.mealOptions || []).slice(0, 6).map((x) => formatSsrDisplayDesc(x?.desc)),
    })));
  }

  return blocks;
}

/** Unique Tripjack segment ids (`key`) present on parsed SSR rows. */
function uniqSsrKeys(arr) {
  return [...new Set((arr || []).map((x) => String(x?.key ?? x?.segmentId ?? '')))].filter(Boolean);
}

/**
 * Flatten all trip-level objects from /review `tripInfos` (any supported shape)
 * for diagnostics — not identical to normalize partition, but covers full supplier tree.
 */
function flattenAllTripLegsFromReviewTripInfos(reviewData) {
  const ti = pickTripInfosFromReview(reviewData);
  if (ti == null) return [];
  const trips = [];
  if (Array.isArray(ti)) {
    ti.forEach((t) => {
      if (t && typeof t === 'object') trips.push(t);
    });
  } else if (typeof ti === 'object') {
    const dictKeys = [
      'ONWARD', 'onward', 'O', 'ONE_WAY', 'DEPARTURE',
      'RETURN', 'return', 'R', 'COMBO',
    ];
    dictKeys.forEach((k) => {
      const arr = ti[k];
      if (Array.isArray(arr)) {
        arr.forEach((t) => {
          if (t && typeof t === 'object') trips.push(t);
        });
      }
    });
  }
  return trips;
}

/**
 * Temporary: end-to-end Tripjack SSR vs seatmap segment trace.
 * Remove when investigation is complete.
 */
function logTripjackSsrInvestigation({
  reviewData,
  bookingData,
  normalized,
  onwardMerged,
  returnMerged,
  isTripjackRT,
}) {
  if (
    !String(
      bookingData?.flightData?.source
      || bookingData?.source
      || bookingData?.supplier
      || bookingData?.flightData?.supplier
      || '',
    ).toLowerCase().includes('tripjack')
    || !reviewData
  ) return;

  const ti = pickTripInfosFromReview(reviewData);
  const tripInfosShape = ti == null
    ? 'missing'
    : (Array.isArray(ti) ? `array(len=${ti.length})` : `object(keys=${Object.keys(ti).join(',')})`);

  const flatTrips = flattenAllTripLegsFromReviewTripInfos(reviewData);
  const supplierPerSegment = [];
  flatTrips.forEach((trip, tripIdx) => {
    (trip.sI || []).forEach((flight, siIdx) => {
      const ssr = flight?.ssrInfo;
      const bagCount = Array.isArray(ssr?.BAGGAGE) ? ssr.BAGGAGE.length : 0;
      const mealCount = Array.isArray(ssr?.MEAL) ? ssr.MEAL.length : 0;
      const seatCount = Array.isArray(ssr?.SEAT) ? ssr.SEAT.length : 0;
      supplierPerSegment.push({
        tripIndex: tripIdx,
        siIndexInTrip: siIdx,
        segmentId: flight?.id,
        fN: flight?.fN,
        da: airportCode(flight?.da),
        aa: airportCode(flight?.aa),
        bagCount,
        mealCount,
        seatCount,
        hasSsrInfoObject: !!(ssr && typeof ssr === 'object' && Object.keys(ssr).length > 0),
      });
    });
  });

  const summarizeNormalizedTrips = (trips) => (trips || []).map((t, i) => ({
    tripIndex: i,
    sICount: Array.isArray(t?.sI) ? t.sI.length : 0,
    segmentIds: (t?.sI || []).map((s) => s?.id),
  }));

  const seatmapPropSegments = bookingData?.flightData?.segments
    || bookingData?.flightData?.tripInfos?.flatMap((t) => t?.sI || [])
    || [];

  const ret = returnMerged || { baggage: [], meals: [], seats: [], extras: [] };

  console.debug('[Tripjack SSR Investigation]', {
    isTripjackRT,
    pickTripInfosShape: tripInfosShape,
    normalizedOnwardTrips: summarizeNormalizedTrips(normalized.onwardTrips),
    normalizedReturnTrips: summarizeNormalizedTrips(normalized.returnTrips),
    supplierReviewFlatTripCount: flatTrips.length,
    supplierPerSegmentSsrCounts: supplierPerSegment,
    supplierTotalSegments: supplierPerSegment.length,
    supplierSegmentsWithBaggage: supplierPerSegment.filter((p) => p.bagCount > 0).length,
    supplierSegmentsWithMeals: supplierPerSegment.filter((p) => p.mealCount > 0).length,
    supplierSegmentsWithSeatSsrRows: supplierPerSegment.filter((p) => p.seatCount > 0).length,
    onwardMergedRowCounts: {
      baggage: onwardMerged.baggage.length,
      meals: onwardMerged.meals.length,
      seats: onwardMerged.seats.length,
      extras: onwardMerged.extras.length,
    },
    onwardMergedUniqueSegmentKeys: {
      baggage: uniqSsrKeys(onwardMerged.baggage),
      meals: uniqSsrKeys(onwardMerged.meals),
      seats: uniqSsrKeys(onwardMerged.seats),
    },
    returnMergedRowCounts: {
      baggage: ret.baggage.length,
      meals: ret.meals.length,
      seats: ret.seats.length,
      extras: ret.extras.length,
    },
    returnMergedUniqueSegmentKeys: {
      baggage: uniqSsrKeys(ret.baggage),
      meals: uniqSsrKeys(ret.meals),
      seats: uniqSsrKeys(ret.seats),
    },
    seatmapSegmentsPropCount: seatmapPropSegments.length,
    dataSources: {
      seatmap: 'GET /api/tripjack/seatmap/{priceId} → response.normalized.segments (tabs); segment labels enriched from bookingData.flightData.segments or tripInfos.sI',
      baggageMealsExtras: 'POST /api/tripjack/review → reviewData tripInfos[*].sI[*].ssrInfo — parsed in AncillaryServices useEffect',
    },
    uiNote: 'Tripjack baggage/meals: itinerary-driven segment cards; SSR rows matched by supplier segment id (key) or full canonical route key only — no blanket copy; dedupe per segment; see SSR_DEBUG.',
  });
}

const AncillaryServices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { agent } = useAgentAuth();

  const { bookingData, reviewData, formConditions } = location.state || {};

  const isTripjack = String(
    bookingData?.flightData?.source
    || bookingData?.source
    || bookingData?.supplier
    || bookingData?.flightData?.supplier
    || '',
  ).toLowerCase().includes('tripjack');

  const [selectedBaggage, setSelectedBaggage] = useState({});
  const [selectedMeals, setSelectedMeals] = useState({});
  const [selectedSeats, setSelectedSeats] = useState({});
  const [selectedExtras, setSelectedExtras] = useState({});
  // G6 · Interactive seat-map selections — keyed by segment, then pax index.
  // Shape: { [segmentKey]: { [paxIdx]: {code, price, key, type} } }
  const [seatMapSelections, setSeatMapSelections] = useState({});
  /** Same order as seatmap tabs (`InteractiveSeatMap` `segmentsForUI`) — SSR grouping fallback when `segments` / `tripInfos` are empty on `flightData`. */
  const [seatmapNormalizedSegments, setSeatmapNormalizedSegments] = useState([]);
  const [baggageProtection, setBaggageProtection] = useState(false);
  const [ssrOptions, setSsrOptions] = useState({
    baggage: [],
    meals: [],
    seats: [],
    extras: [],
    baggageProtection: null,
  });

  // Riya pre-booking SSR loader state. We reuse the same UI as Tripjack —
  // the only difference is the catalogue source: Riya pulls from
  // POST /api/riya/price → response.ssr.{baggage,meals,other}.
  const [riyaSsrLoading, setRiyaSsrLoading] = useState(false);
  const [riyaSsrError, setRiyaSsrError] = useState(null);

  // Sprint-3 · Riya RT — return-leg catalogue + selections kept separate
  // from the outbound state so we can emit `returnSsrSelections` on /book.
  // `activeLeg` toggles which catalogue + selections the picker UI uses;
  // both legs' selections persist independently.
  const [returnSsrOptions, setReturnSsrOptions] = useState({
    baggage: [], meals: [], seats: [], extras: [], baggageProtection: null,
  });
  const [returnSelectedBaggage, setReturnSelectedBaggage] = useState({});
  const [returnSelectedMeals, setReturnSelectedMeals] = useState({});
  /** Tripjack segmented meals only — { [segmentKey]: { [paxIdx]: meal } } per bound (flat list unchanged). */
  const [tripjackSegmentedMealSelections, setTripjackSegmentedMealSelections] = useState({});
  const [returnTripjackSegmentedMealSelections, setReturnTripjackSegmentedMealSelections] = useState({});
  /** Tripjack segmented baggage only — { [segmentKey]: { [paxIdx]: bag } } per bound (flat list unchanged). */
  const [tripjackSegmentedBaggageSelections, setTripjackSegmentedBaggageSelections] = useState({});
  const [returnTripjackSegmentedBaggageSelections, setReturnTripjackSegmentedBaggageSelections] = useState({});
  const [activeLeg, setActiveLeg] = useState('outbound');
  const [returnSelectedExtras, setReturnSelectedExtras] = useState({});

  // Convenience: is this a Riya round-trip booking?
  const isRiyaRT = !!(
    bookingData?.flightData?.source === 'riya'
    && (bookingData?.flightData?.return || bookingData?.bookingType === 'roundtrip')
  );

  // Tripjack RT: support per-leg baggage/meal/extras selection (industry standard).
  const isTripjackRT = !!(
    isTripjack
    && bookingData?.bookingType === 'roundtrip'
    && Array.isArray(bookingData?.priceIds)
    && bookingData.priceIds.length === 2
  );

  useEffect(() => {
    if (!bookingData) {
      console.error("❌ No booking data - redirecting to home");
      navigate("/");
      return;
    }

    console.log("✅ Booking data received:", bookingData);
    console.log("✅ Review data:", reviewData ? "Present" : "Not available");

    const src = bookingData?.flightData?.source;

    // Offline flights have no ancillary concept — forward straight to review.
    if (src === 'offline') {
      console.log(`⏭️  ${src} flight in Ancillary — redirecting to review`);
      navigate('/review-booking', {
        state: {
          bookingData: {
            ...bookingData,
            ssrSelections: null,
            addOnsIds: null,
          },
        },
        replace: true,
      });
      return;
    }

    // Tripjack GDS (non-LCC): no book-time SSR — same path as offline (review only).
    const srcLower = String(src || '').toLowerCase();
    if (srcLower.includes('tripjack') && formConditions?.ssr_booking_allowed === false) {
      navigate('/review-booking', {
        state: {
          bookingData: {
            ...bookingData,
            ssrSelections: null,
            addOnsIds: null,
          },
        },
        replace: true,
      });
      return;
    }

    // Riya flights — fetch the catalogue from /api/riya/price and map it
    // into the existing Tripjack-shaped ssrOptions so the rest of the page
    // (UI, calculations, navigation) renders identically. We deliberately
    // skip seat selection here — Riya seat-map is a separate API call and
    // is not yet wired into this page.
    if (src === 'riya') {
      const flight = bookingData.flightData;
      const passengers = bookingData.passengersRaw || bookingData.passengers || [];
      const adults = passengers.filter(p => (p.type || p.pt || 'ADULT').toUpperCase().startsWith('A')).length || 1;
      const children = passengers.filter(p => (p.type || p.pt || '').toUpperCase().startsWith('C')).length;
      const infants = passengers.filter(p => (p.type || p.pt || '').toUpperCase().startsWith('I')).length;

      setRiyaSsrLoading(true);
      setRiyaSsrError(null);
      (async () => {
        try {
          const resp = await axios.post(`${API}/riya/price`, {
            flight,
            adults,
            children,
            infants,
          }, { withCredentials: true, validateStatus: () => true, timeout: 35000 });
          if (resp.status < 200 || resp.status >= 300) {
            const detail = resp.data?.detail || resp.data?.message;
            const supplierDown = resp.status === 502
              || /Proxy Error|Login|TravelAPI\.svc|ReadTimeout|Bad Gateway/i.test(String(detail || ''));
            throw new Error(supplierDown
              ? 'Riya supplier is temporarily unreachable. You can skip add-ons and continue.'
              : (typeof detail === 'string' ? detail : 'Failed to load Riya add-ons'));
          }
          const ssr = resp.data?.ssr || {};
          // Map Riya catalogue → Tripjack-shaped {code, desc, amount, key}
          // so the existing render logic / handleContinue can stay generic.
          const mapItem = (item) => ({
            code: item.Code || String(item.MealID || item.BaggageID || item.Id || ''),
            desc: item.Description || item.Code || `SSR ${item.MealID || item.BaggageID || item.Id || ''}`,
            amount: Number(item.Amount || 0) || 0,
            key: String(item.MealID || item.BaggageID || item.Id || item.Code || ''),
            // Preserve the original payload so handleContinue can emit Riya format
            _riyaRaw: item,
          });
          setSsrOptions({
            baggage: (ssr.baggage || []).map(mapItem),
            meals: (ssr.meals || []).map(mapItem),
            seats: [],
            baggageProtection: null,
          });
        } catch (e) {
          setRiyaSsrError(e.message || 'Could not load Riya add-ons.');
          // Empty catalogue — user can still skip & continue
          setSsrOptions({ baggage: [], meals: [], seats: [], baggageProtection: null });
        } finally {
          setRiyaSsrLoading(false);
        }
      })();

      // Sprint-3 · Riya RT — fetch return-leg catalogue separately. The Riya
      // /price endpoint is single-flight; for RT we must call it twice.
      const returnFlight = bookingData.flightData?.return;
      if (returnFlight && (returnFlight.riyaFlightId || returnFlight.id)) {
        (async () => {
          try {
            const r = await axios.post(`${API}/riya/price`, {
              flight: returnFlight,
              adults, children, infants,
            }, { withCredentials: true, validateStatus: () => true, timeout: 35000 });
            if (r.status >= 200 && r.status < 300) {
              const rssr = r.data?.ssr || {};
              const mapItem = (item) => ({
                code: item.Code || String(item.MealID || item.BaggageID || item.Id || ''),
                desc: item.Description || item.Code || `SSR ${item.MealID || item.BaggageID || item.Id || ''}`,
                amount: Number(item.Amount || 0) || 0,
                key: String(item.MealID || item.BaggageID || item.Id || item.Code || ''),
                _riyaRaw: item,
              });
              setReturnSsrOptions({
                baggage: (rssr.baggage || []).map(mapItem),
                meals: (rssr.meals || []).map(mapItem),
                seats: [],
                extras: [],
                baggageProtection: null,
              });
            }
          } catch (e) {
            console.warn('[riya-rt] return-leg /price failed:', e?.message);
          }
        })();
      }
      return;
    }

    // Parse SSR options from review data
    const options = {
      baggage: [],
      meals: [],
      seats: [],
      extras: [],
      baggageProtection: null,
    };

    // Check if reviewData exists and has addOnsIds
    if (reviewData) {
      // Extract baggage protection from addOnsIds
      if (reviewData.addOnsIds?.bp) {
        options.baggageProtection = {
          price: reviewData.addOnsIds.bp,
          available: true,
        };
      }

      // Extract SSR options from tripInfos — merge every segment in the leg.
      //
      // Tripjack PDF line 933-935: `ssrBaggageInfos[].key` (and analogous meal/seat
      // rows on /book) must be the SEGMENT ID (`sI.id`), not the SSR code. Each
      // catalogue row below stamps `key: segmentId` so the user picks the row
      // that matches the segment Tripjack expects.
      //
      // Multi-segment legs: accumulate BAGGAGE / MEAL / SEAT / EXTRAS from *all*
      // segments so the UI shows the full supplier catalogue (e.g. 29 meal codes
      // spread across segments), not only the first segment that had SSR.
      const parseLegFromTripInfo = (tripInfo) => {
        const out = { baggage: [], meals: [], seats: [], extras: [] };
        if (!tripInfo || !Array.isArray(tripInfo.sI)) return out;
        for (const flight of tripInfo.sI) {
          const segmentId = flight?.id; // PDF 839 — per-segment id
          const ssr = flight?.ssrInfo;
          if (!segmentId || !ssr) continue;

          if (Array.isArray(ssr.BAGGAGE) && ssr.BAGGAGE.length > 0) {
            out.baggage.push(
              ...ssr.BAGGAGE.map((bag) => {
                const segmentIdStr = flight?.id != null ? String(flight.id) : '';
                return {
                  code: bag.code,
                  desc: bag.desc,
                  amount: Number(bag.amount || 0) || 0,
                  /** /book expects Tripjack `sI.id`, not baggage product id. */
                  key: segmentIdStr,
                  segmentId: segmentIdStr,
                  bookingSegmentId: segmentIdStr,
                  da: flight?.da,
                  aa: flight?.aa,
                  fN: flight?.fN ?? flight?.fD?.fN,
                  fD: flight?.fD,
                  al: flight?.al,
                  dt: flight?.dt,
                  tripIndex: bag.tripIndex ?? bag.trip_index ?? bag.tripIdx,
                  segmentIndex: bag.segmentIndex ?? bag.segment_index ?? bag.segIdx ?? bag.siIdx,
                  wayType: bag.wayType ?? bag.way_type,
                  _tjBaggageProductId: bag.id ?? bag.baggageId ?? bag.ssrKey ?? bag.ssr_key,
                  _tjSsrRawRow: SSR_DEBUG ? { ...bag } : undefined,
                };
              })
            );
          }
          if (Array.isArray(ssr.MEAL) && ssr.MEAL.length > 0) {
            out.meals.push(
              ...ssr.MEAL.map((meal) => {
                const segmentIdStr = flight?.id != null ? String(flight.id) : '';
                return {
                  code: meal.code,
                  desc: meal.desc,
                  amount: Number(meal.amount || 0) || 0,
                  key: segmentIdStr,
                  segmentId: segmentIdStr,
                  bookingSegmentId: segmentIdStr,
                  da: flight?.da,
                  aa: flight?.aa,
                  fN: flight?.fN ?? flight?.fD?.fN,
                  fD: flight?.fD,
                  al: flight?.al,
                  dt: flight?.dt,
                  tripIndex: meal.tripIndex ?? meal.trip_index,
                  segmentIndex: meal.segmentIndex ?? meal.segment_index,
                  wayType: meal.wayType ?? meal.way_type,
                  _tjMealProductId: meal.id ?? meal.mealId ?? meal.ssrKey ?? meal.ssr_key,
                  _tjSsrRawRow: SSR_DEBUG ? { ...meal } : undefined,
                };
              })
            );
          }
          if (Array.isArray(ssr.SEAT) && ssr.SEAT.length > 0) {
            out.seats.push(
              ...ssr.SEAT.map((seat) => ({
                code: seat.code,
                desc: seat.desc,
                amount: Number(seat.amount || 0) || 0,
                key: segmentId,
                segmentId,
                isLegroom: seat.isLegroom || false,
              }))
            );
          }
          const extraList = ssr.EXTRASERVICES || ssr.EXTRASERVICE || ssr.EXTRA_SERVICE;
          if (Array.isArray(extraList) && extraList.length > 0) {
            out.extras.push(
              ...extraList.map((ex) => ({
                code: ex.code,
                desc: ex.desc,
                amount: Number(ex.amount || 0) || 0,
                key: segmentId,
                segmentId,
              }))
            );
          }
        }
        return out;
      };

      const normalized = normalizeTripjackTripInfos(reviewData, bookingData, isTripjackRT);

      const onwardMerged = mergeTripsToSsrOptions(normalized.onwardTrips, parseLegFromTripInfo);
      options.baggage = onwardMerged.baggage;
      options.meals = onwardMerged.meals;
      options.seats = onwardMerged.seats;
      options.extras = onwardMerged.extras;

      let returnMerged = null;
      if (isTripjackRT && normalized.returnTrips.length > 0) {
        returnMerged = mergeTripsToSsrOptions(normalized.returnTrips, parseLegFromTripInfo);
        setReturnSsrOptions({
          baggage: returnMerged.baggage,
          meals: returnMerged.meals,
          seats: returnMerged.seats,
          extras: returnMerged.extras,
          baggageProtection: null,
        });
      } else if (!isRiyaRT) {
        setReturnSsrOptions({
          baggage: [],
          meals: [],
          seats: [],
          extras: [],
          baggageProtection: null,
        });
      }

      logTripjackSsrInvestigation({
        reviewData,
        bookingData,
        normalized,
        onwardMerged,
        returnMerged,
        isTripjackRT,
      });
    } else {
      console.warn("⚠️ No review data - SSR options will not be available");
    }

    console.log("📦 SSR Options parsed:", {
      baggageCount: options.baggage.length,
      mealsCount: options.meals.length,
      seatsCount: options.seats.length,
      extrasCount: options.extras.length,
      hasBaggageProtection: !!options.baggageProtection,
    });

    setSsrOptions(options);
  }, [bookingData, reviewData, formConditions, navigate, isTripjackRT]);

  const calculateTotal = () => {
    let total = bookingData?.totalAmount || 0;

    // Add baggage protection
    if (baggageProtection && ssrOptions.baggageProtection) {
      total += ssrOptions.baggageProtection.price;
    }

    // Add selected baggage (segment cards use nested state; journey mode uses flat map)
    if (tripjackNestedBaggageHasAnySelection(tripjackSegmentedBaggageSelections)) {
      total += sumTripjackNestedBaggageSelections(tripjackSegmentedBaggageSelections);
    } else {
      Object.values(selectedBaggage).forEach((baggageOption) => {
        if (baggageOption) {
          total += baggageOption.amount;
        }
      });
    }

    // Add selected meals for each passenger (Tripjack segment cards use nested state)
    if (tripjackNestedMealsHasAnySelection(tripjackSegmentedMealSelections)) {
      total += sumTripjackNestedMealSelections(tripjackSegmentedMealSelections);
    } else {
      Object.values(selectedMeals).forEach((mealOption) => {
        if (mealOption) {
          total += mealOption.amount;
        }
      });
    }

    // Add selected seats for each passenger
    Object.values(selectedSeats).forEach((seatOption) => {
      if (seatOption) {
        total += seatOption.amount;
      }
    });

    Object.values(selectedExtras).forEach((extraOption) => {
      if (extraOption) {
        total += extraOption.amount;
      }
    });

    // Tripjack RT: include return-leg baggage/meals/extras too.
    if (isTripjackRT) {
      if (tripjackNestedBaggageHasAnySelection(returnTripjackSegmentedBaggageSelections)) {
        total += sumTripjackNestedBaggageSelections(returnTripjackSegmentedBaggageSelections);
      } else {
        Object.values(returnSelectedBaggage).forEach((baggageOption) => {
          if (baggageOption) total += baggageOption.amount;
        });
      }
      if (tripjackNestedMealsHasAnySelection(returnTripjackSegmentedMealSelections)) {
        total += sumTripjackNestedMealSelections(returnTripjackSegmentedMealSelections);
      } else {
        Object.values(returnSelectedMeals).forEach((mealOption) => {
          if (mealOption) total += mealOption.amount;
        });
      }
      Object.values(returnSelectedExtras).forEach((extraOption) => {
        if (extraOption) total += extraOption.amount;
      });
    }

    // G6 · Add interactive seat-map selections (per segment per pax)
    Object.values(seatMapSelections).forEach((segPicks) => {
      if (!segPicks) return;
      Object.values(segPicks).forEach((seat) => {
        if (seat?.price) total += Number(seat.price) || 0;
      });
    });

    return total;
  };

  const ancillaryFareDisplay = useMemo(() => {
    if (!bookingData?.flightData) return null;
    const fd = bookingData.flightData;
    const bt = bookingData.bookingType || fd.type || fd.bookingType || "oneway";
    const paxSrc = bookingData.passengersRaw || bookingData.passengers;
    let adults = 0;
    let children = 0;
    let infants = 0;
    if (Array.isArray(paxSrc)) {
      const d = derivePaxCountsFromPassengers(paxSrc);
      adults = d.adults;
      children = d.children;
      infants = d.infants;
    } else if (paxSrc && typeof paxSrc === "object") {
      adults = Number(paxSrc.adults) || 0;
      children = Number(paxSrc.children) || 0;
      infants = Number(paxSrc.infants) || 0;
    }
    if (adults === 0 && children === 0 && infants === 0) {
      adults = Number(fd.adults || fd._paxCount?.adults || 1) || 1;
      children = Number(fd.children || fd._paxCount?.children || 0) || 0;
      infants = Number(fd.infants || fd._paxCount?.infants || 0) || 0;
    }
    return deriveDisplayFare({
      flightData: fd,
      bookingType: bt,
      numAdults: adults,
      numChildren: children,
      numInfants: infants,
    });
  }, [bookingData]);

  const ancillaryPayTotal = calculateTotal();
  const commissionFare = deriveCommissionFare({
    amountToPay: ancillaryPayTotal,
    commissionPreview: resolveCommissionPreview(reviewData, bookingData, agent),
  });

  const handleContinue = () => {
    const src = bookingData?.flightData?.source;

    // Riya path — emit selections in Riya /Book payload format. The backend
    // already accepts ssrSelections.{baggage,meals,other,seats}[] with
    // {PaxRefNumber, BaggageID|MealID|OtherSSRID, Amount, Description} rows.
    if (src === 'riya') {
      // Sprint-3 · Reusable per-leg builder so outbound + return share logic.
      const buildLegPayload = (selBag, selMeals) => {
        const baggage = [];
        const meals = [];
        const other = [];
        Object.entries(selBag).forEach(([paxIdx, item]) => {
          if (!item || !item._riyaRaw) return;
          baggage.push({
            PaxRefNumber: String(parseInt(paxIdx, 10) + 1),
            BaggageID: String(item._riyaRaw.BaggageID || item._riyaRaw.Id || item._riyaRaw.Code || ''),
            Code: String(item._riyaRaw.Code || item._riyaRaw.Description || ''),
            Amount: String(item._riyaRaw.Amount || item.amount || 0),
            Description: item._riyaRaw.Description || item.desc || '',
          });
        });
        Object.entries(selMeals).forEach(([paxIdx, item]) => {
          if (!item || !item._riyaRaw) return;
          meals.push({
            PaxRefNumber: String(parseInt(paxIdx, 10) + 1),
            MealID: String(item._riyaRaw.MealID || item._riyaRaw.Id || item._riyaRaw.Code || ''),
            Code: String(item._riyaRaw.Code || item._riyaRaw.Description || ''),
            Amount: String(item._riyaRaw.Amount || item.amount || 0),
            Description: item._riyaRaw.Description || item.desc || '',
          });
        });
        const total = baggage.reduce((s, x) => s + Number(x.Amount || 0), 0)
                    + meals.reduce((s, x) => s + Number(x.Amount || 0), 0)
                    + other.reduce((s, x) => s + Number(x.Amount || 0), 0);
        return { baggage, meals, other, seats: [], total };
      };

      const outbound = buildLegPayload(selectedBaggage, selectedMeals);
      const ret = isRiyaRT
        ? buildLegPayload(returnSelectedBaggage, returnSelectedMeals)
        : null;
      const ssrTotal = outbound.total + (ret ? ret.total : 0);

      navigate("/review-booking", {
        state: {
          bookingData: {
            ...bookingData,
            ssrSelections: { baggage: outbound.baggage, meals: outbound.meals, other: outbound.other, seats: [] },
            // Sprint-3 · Riya RT — emit return-leg selections in same shape
            // so backend `/api/riya/book-b2c` (riya_booking.py:706) receives
            // them. Backend already accepts `returnSsrSelections`; FE is now
            // wired all the way from the picker UI to the supplier call.
            returnSsrSelections: ret
              ? { baggage: ret.baggage, meals: ret.meals, other: ret.other, seats: [] }
              : (bookingData.returnSsrSelections || null),
            ssrTotalAmount: ssrTotal,
            totalAmount: (bookingData.totalAmount || 0) + ssrTotal,
            addOnsIds: null,
          },
          fromAncillary: true,
        },
      });
      return;
    }

    // Tripjack path — original behaviour: prepare SSR data per passenger
    const passengers = bookingData?.passengersRaw || [];

    const tjSrc = isTripjack;
    const tjOutBlocks = tjSrc
      ? buildTripjackSegmentAncillaryBlocks(
        ssrOptions.baggage || [],
        ssrOptions.meals || [],
        visibleFlightSegments,
      )
      : [];
    const tjRetBlocks = tjSrc && isTripjackRT
      ? buildTripjackSegmentAncillaryBlocks(
        returnSsrOptions.baggage || [],
        returnSsrOptions.meals || [],
        visibleFlightSegments,
      )
      : [];

    const outboundMealsForSsrSelections = tripjackNestedMealsHasAnySelection(tripjackSegmentedMealSelections)
      ? expandNestedTripjackMealsToSsrSelectionsMap(tjOutBlocks, tripjackSegmentedMealSelections)
      : selectedMeals;
    const returnMealsForSsrSelections = isTripjackRT
      ? (tripjackNestedMealsHasAnySelection(returnTripjackSegmentedMealSelections)
        ? expandNestedTripjackMealsToSsrSelectionsMap(tjRetBlocks, returnTripjackSegmentedMealSelections)
        : returnSelectedMeals)
      : returnSelectedMeals;

    const outboundBaggageForSsrSelections = tripjackNestedBaggageHasAnySelection(tripjackSegmentedBaggageSelections)
      ? expandNestedTripjackBaggageToSsrSelectionsMap(tjOutBlocks, tripjackSegmentedBaggageSelections)
      : selectedBaggage;
    const returnBaggageForSsrSelections = isTripjackRT
      ? (tripjackNestedBaggageHasAnySelection(returnTripjackSegmentedBaggageSelections)
        ? expandNestedTripjackBaggageToSsrSelectionsMap(tjRetBlocks, returnTripjackSegmentedBaggageSelections)
        : returnSelectedBaggage)
      : returnSelectedBaggage;

    const hasSegmentSeatSelections = Object.values(seatMapSelections || {}).some(
      (seg) => seg && typeof seg === 'object' && Object.keys(seg).length > 0,
    );

    // Build SSR arrays for each passenger
    const passengersWithSSR = passengers.map((passenger, idx) => {
      const passengerSSR = {
        ...bookingData.passengers[idx], // Original passenger data from BookingPage
        ssrBaggageInfos: [],
        ssrMealInfos: [],
        ssrSeatInfos: [],
        ssrExtraServiceInfos: [],
      };

      // Add selected baggage
      // Tripjack v2.0 PDF line 933-935: `ssrBaggageInfos[].key` MUST be
      // the segment id (`sI.id` from /review). The parser at the top of
      // this file stamps `selectedBaggage[idx].key = segmentId` for every
      // baggage option. The fallback `|| selectedBaggage[idx].code` was
      // historically a safety net but became the BUG-source in May-2026
      // (errCode 1129 "Baggage not allowed for connecting segment or
      // invalid baggage ssr data"): when key was undefined, the SSR
      // code ended up there which Tripjack rejects. Drop the fallback —
      // if there's no segment id, omit the baggage selection entirely so
      // /book doesn't fail.
      if (tripjackNestedBaggageHasAnySelection(tripjackSegmentedBaggageSelections)) {
        collectTripjackMealSsrRowsForPassenger(tjOutBlocks, tripjackSegmentedBaggageSelections, idx).forEach((row) => {
          passengerSSR.ssrBaggageInfos.push(row);
        });
      } else if (selectedBaggage[idx]?.key) {
        passengerSSR.ssrBaggageInfos.push({
          key: selectedBaggage[idx].key,
          code: selectedBaggage[idx].code,
        });
      }
      if (isTripjackRT) {
        if (tripjackNestedBaggageHasAnySelection(returnTripjackSegmentedBaggageSelections)) {
          collectTripjackMealSsrRowsForPassenger(tjRetBlocks, returnTripjackSegmentedBaggageSelections, idx).forEach((row) => {
            passengerSSR.ssrBaggageInfos.push(row);
          });
        } else if (returnSelectedBaggage[idx]?.key) {
          passengerSSR.ssrBaggageInfos.push({
            key: returnSelectedBaggage[idx].key,
            code: returnSelectedBaggage[idx].code,
          });
        }
      }

      // Add selected meal(s) — Tripjack segment cards: one row per segment per passenger
      if (tripjackNestedMealsHasAnySelection(tripjackSegmentedMealSelections)) {
        collectTripjackMealSsrRowsForPassenger(tjOutBlocks, tripjackSegmentedMealSelections, idx).forEach((row) => {
          passengerSSR.ssrMealInfos.push(row);
        });
      } else if (selectedMeals[idx]?.key) {
        passengerSSR.ssrMealInfos.push({
          key: selectedMeals[idx].key,
          code: selectedMeals[idx].code,
        });
      }
      if (isTripjackRT) {
        if (tripjackNestedMealsHasAnySelection(returnTripjackSegmentedMealSelections)) {
          collectTripjackMealSsrRowsForPassenger(tjRetBlocks, returnTripjackSegmentedMealSelections, idx).forEach((row) => {
            passengerSSR.ssrMealInfos.push(row);
          });
        } else if (returnSelectedMeals[idx]?.key) {
          passengerSSR.ssrMealInfos.push({
            key: returnSelectedMeals[idx].key,
            code: returnSelectedMeals[idx].code,
          });
        }
      }

      // Legacy flat seat SSR — only when interactive segmented seatmap has no picks
      if (!hasSegmentSeatSelections && selectedSeats[idx]?.key) {
        passengerSSR.ssrSeatInfos.push({
          key: normalizeTripjackSeatSegmentKey(selectedSeats[idx].key),
          code: selectedSeats[idx].code,
        });
      }

      // Interactive seatmap: use Tripjack review segment id on each pick, not UI tab key
      Object.entries(seatMapSelections || {}).forEach(([segKey, picks]) => {
        const seat = picks?.[idx];
        if (seat?.code) {
          const normalizedSeatKey =
            seat.bookingSegmentId
            || normalizeTripjackSeatSegmentKey(segKey)
            || normalizeTripjackSeatSegmentKey(seat.key);
          passengerSSR.ssrSeatInfos.push({
            key: normalizeTripjackSeatSegmentKey(normalizedSeatKey),
            code: seat.code,
          });
        }
      });

      passengerSSR.ssrSeatInfos = (passengerSSR.ssrSeatInfos || []).map((s) => ({
        ...s,
        key: normalizeTripjackSeatSegmentKey(s?.key),
      }));
      console.log("[FINAL NORMALIZED SEATS]", passengerSSR.ssrSeatInfos);

      if (selectedExtras[idx] && selectedExtras[idx].key) {
        passengerSSR.ssrExtraServiceInfos.push({
          key: selectedExtras[idx].key,
          code: selectedExtras[idx].code,
        });
      }
      if (isTripjackRT && returnSelectedExtras[idx] && returnSelectedExtras[idx].key) {
        passengerSSR.ssrExtraServiceInfos.push({
          key: returnSelectedExtras[idx].key,
          code: returnSelectedExtras[idx].code,
        });
      }

      return passengerSSR;
    });

    const looksLikeTripjackPriceIdConsolidate = (v) =>
      typeof v === 'string' && v.includes('~') && v.length > 30;
    const inferTripjackFromIds =
      String(bookingData?.flightData?.source || bookingData?.flightData?.supplier || '').toLowerCase().includes('tripjack')
      || looksLikeTripjackPriceIdConsolidate(bookingData?.priceId)
      || looksLikeTripjackPriceIdConsolidate(bookingData?.flightData?.priceId)
      || looksLikeTripjackPriceIdConsolidate(bookingData?.onwardPriceId)
      || looksLikeTripjackPriceIdConsolidate(bookingData?.returnPriceId)
      || (Array.isArray(bookingData?.priceIds) ? bookingData.priceIds : []).some(looksLikeTripjackPriceIdConsolidate);
    const rootPriceId = bookingData.priceId ?? bookingData.flightData?.priceId;
    const mergedPriceIds = bookingData.priceIds ?? bookingData.flightData?.priceIds;

    // Update booking data with SSR selections (preserve supplier routing fields for ReviewBooking)
    const updatedBookingData = {
      ...bookingData,
      priceId: rootPriceId,
      priceIds: mergedPriceIds,
      onwardPriceId: bookingData.onwardPriceId,
      returnPriceId: bookingData.returnPriceId,
      passengers: passengersWithSSR, // Update with SSR-enriched passenger data
      flightData: {
        ...bookingData.flightData,
        source:
          bookingData.flightData?.source
          || (inferTripjackFromIds ? 'tripjack' : bookingData.flightData?.supplier),
        priceId: bookingData.flightData?.priceId ?? bookingData.priceId,
        priceIds: bookingData.flightData?.priceIds ?? bookingData.priceIds,
      },
      ssrSelections: {
        baggage: outboundBaggageForSsrSelections,
        meals: outboundMealsForSsrSelections,
        seats: selectedSeats,
        extras: selectedExtras,
        baggageProtection: baggageProtection,
      },
      returnSsrSelections: isTripjackRT ? {
        baggage: returnBaggageForSsrSelections,
        meals: returnMealsForSsrSelections,
        extras: returnSelectedExtras,
      } : (bookingData.returnSsrSelections || null),
      totalAmount: calculateTotal(),
      addOnsIds: {},
    };

    // Add baggage protection if selected
    if (baggageProtection && ssrOptions.baggageProtection) {
      updatedBookingData.addOnsIds.bp = ssrOptions.baggageProtection.price;
    }

    console.log('[TRIPJACK SEAT SSR FINAL]', {
      passengerSSR: passengersWithSSR.map((p) => ({
        ssrSeatInfos: p.ssrSeatInfos,
      })),
      seatMapSelections,
    });

    // Navigate to ReviewBooking
    navigate("/review-booking", {
      state: {
        bookingData: updatedBookingData,
        fromAncillary: true,
      },
    });
  };

  const handleSkip = () => {
    navigate("/review-booking", {
      state: {
        bookingData: {
          ...bookingData,
          ssrData: null,
        },
        fromAncillary: true,
      },
    });
  };

  const passengers = bookingData?.passengersRaw || [];

  // Tripjack RT: switch add-on pickers between outbound and return legs.
  const isReturnLegActive = isTripjackRT && activeLeg === 'return';
  const currentBaggageOptions = isReturnLegActive ? (returnSsrOptions.baggage || []) : (ssrOptions.baggage || []);
  const currentMealOptions = isReturnLegActive ? (returnSsrOptions.meals || []) : (ssrOptions.meals || []);
  const currentExtrasOptions = isReturnLegActive ? (returnSsrOptions.extras || []) : (ssrOptions.extras || []);

  const currentSelectedBaggage = isReturnLegActive ? returnSelectedBaggage : selectedBaggage;
  const currentSelectedMeals = isReturnLegActive ? returnSelectedMeals : selectedMeals;
  const currentSelectedExtras = isReturnLegActive ? returnSelectedExtras : selectedExtras;

  const setCurrentSelectedBaggage = (updater) => {
    if (isReturnLegActive) setReturnSelectedBaggage(updater);
    else setSelectedBaggage(updater);
  };
  const setCurrentSelectedMeals = (updater) => {
    if (isReturnLegActive) setReturnSelectedMeals(updater);
    else setSelectedMeals(updater);
  };
  const setCurrentSelectedExtras = (updater) => {
    if (isReturnLegActive) setReturnSelectedExtras(updater);
    else setSelectedExtras(updater);
  };

  const handleSeatmapSegmentsNormalized = useCallback((segs) => {
    if (Array.isArray(segs) && segs.length > 0) setSeatmapNormalizedSegments(segs);
  }, []);

  const reviewPayloadForSegments = reviewData || bookingData?.reviewData;

  const itineraryOnlyFlightSegments = useMemo(() => {
    const fd = bookingData?.flightData;
    return extractVisibleFlightSegments({
      flightData: fd,
      bookingData,
      reviewData: reviewPayloadForSegments,
      seatmapEnrichedSegments: null,
    });
  }, [bookingData, reviewPayloadForSegments]);

  const seatmapDerivedFlightSegments = useMemo(() => {
    if (!seatmapNormalizedSegments?.length) return [];
    const fd = bookingData?.flightData;
    return extractVisibleFlightSegments({
      flightData: fd,
      bookingData,
      reviewData: reviewPayloadForSegments,
      seatmapEnrichedSegments: seatmapNormalizedSegments,
    });
  }, [bookingData, reviewPayloadForSegments, seatmapNormalizedSegments]);

  /** Prefer richer segment list (seatmap often has per-leg tabs when `flightData.segments` is empty). */
  const visibleFlightSegments = useMemo(() => {
    const itin = itineraryOnlyFlightSegments;
    const sm = seatmapDerivedFlightSegments;
    if (sm.length > itin.length) return sm;
    if (itin.length > 0) return itin;
    return sm;
  }, [itineraryOnlyFlightSegments, seatmapDerivedFlightSegments]);

  const tripjackSegmentedBlocks = useMemo(
    () => buildTripjackSegmentAncillaryBlocks(
      currentBaggageOptions,
      currentMealOptions,
      visibleFlightSegments,
    ),
    [visibleFlightSegments, currentBaggageOptions, currentMealOptions],
  );

  /** Per-bound segment blocks for collapse / sidebar (independent of active leg tab). */
  const tripjackOutboundSegmentBlocks = useMemo(
    () => buildTripjackSegmentAncillaryBlocks(
      ssrOptions.baggage || [],
      ssrOptions.meals || [],
      visibleFlightSegments,
    ),
    [visibleFlightSegments, ssrOptions.baggage, ssrOptions.meals],
  );
  const tripjackReturnSegmentBlocks = useMemo(
    () => (
      isTripjackRT
        ? buildTripjackSegmentAncillaryBlocks(
          returnSsrOptions.baggage || [],
          returnSsrOptions.meals || [],
          visibleFlightSegments,
        )
        : []
    ),
    [visibleFlightSegments, isTripjackRT, returnSsrOptions.baggage, returnSsrOptions.meals],
  );

  const tripjackOutboundBaggageMode = useMemo(
    () => detectTripjackBaggageMode({
      baggageOptions: ssrOptions.baggage || [],
      segmentBlocks: tripjackOutboundSegmentBlocks,
    }),
    [ssrOptions.baggage, tripjackOutboundSegmentBlocks],
  );
  const tripjackReturnBaggageMode = useMemo(
    () => (
      isTripjackRT
        ? detectTripjackBaggageMode({
          baggageOptions: returnSsrOptions.baggage || [],
          segmentBlocks: tripjackReturnSegmentBlocks,
        })
        : 'segment'
    ),
    [isTripjackRT, returnSsrOptions.baggage, tripjackReturnSegmentBlocks],
  );

  const outboundMealSidebarRows = useMemo(
    () => (
      tripjackNestedMealsHasAnySelection(tripjackSegmentedMealSelections)
        ? mealSidebarRowsFromBlocksAndNested(
          tripjackOutboundSegmentBlocks,
          tripjackSegmentedMealSelections,
          passengers,
        )
        : []
    ),
    [tripjackOutboundSegmentBlocks, tripjackSegmentedMealSelections, passengers],
  );
  const returnMealSidebarRows = useMemo(
    () => (
      isTripjackRT && tripjackNestedMealsHasAnySelection(returnTripjackSegmentedMealSelections)
        ? mealSidebarRowsFromBlocksAndNested(
          tripjackReturnSegmentBlocks,
          returnTripjackSegmentedMealSelections,
          passengers,
        )
        : []
    ),
    [isTripjackRT, tripjackReturnSegmentBlocks, returnTripjackSegmentedMealSelections, passengers],
  );
  const outboundBaggageSidebarRows = useMemo(
    () => (
      tripjackNestedBaggageHasAnySelection(tripjackSegmentedBaggageSelections)
        ? baggageSidebarRowsFromBlocksAndNested(
          tripjackOutboundSegmentBlocks,
          tripjackSegmentedBaggageSelections,
          passengers,
        )
        : []
    ),
    [tripjackOutboundSegmentBlocks, tripjackSegmentedBaggageSelections, passengers],
  );
  const returnBaggageSidebarRows = useMemo(
    () => (
      isTripjackRT && tripjackNestedBaggageHasAnySelection(returnTripjackSegmentedBaggageSelections)
        ? baggageSidebarRowsFromBlocksAndNested(
          tripjackReturnSegmentBlocks,
          returnTripjackSegmentedBaggageSelections,
          passengers,
        )
        : []
    ),
    [isTripjackRT, tripjackReturnSegmentBlocks, returnTripjackSegmentedBaggageSelections, passengers],
  );

  const tripjackBaggageMode = isReturnLegActive ? tripjackReturnBaggageMode : tripjackOutboundBaggageMode;

  // Leg-aware blocks for the active leg (drives leg-global card display).
  const tripjackActiveLegBlocks = isReturnLegActive
    ? tripjackReturnSegmentBlocks
    : tripjackOutboundSegmentBlocks;

  const journeyBaggageDisplayOptions = useMemo(
    () => (
      tripjackBaggageMode === 'journey'
        ? buildLegBaggageDisplayOptions(tripjackActiveLegBlocks)
        : []
    ),
    [tripjackBaggageMode, tripjackActiveLegBlocks],
  );

  /**
   * When the leg-global picker fires, fan the choice out to every segment
   * of the active leg by writing each segment's native priced row into
   * `tripjackSegmentedBaggageSelections`. This keeps the supplier-side
   * payload contract intact (one ssrBaggageInfos per segment per pax)
   * while presenting a single leg-level choice to the user.
   */
  const setLegBaggageForPassenger = useCallback((paxIdx, legGlobalOption) => {
    const updater = (prev) => {
      const next = { ...(prev || {}) };
      tripjackActiveLegBlocks.forEach((b) => {
        const segMap = next[b.segmentKey] ? { ...next[b.segmentKey] } : {};
        if (!legGlobalOption) {
          delete segMap[paxIdx];
        } else {
          const perSegRow = legGlobalOption?._legSegmentPriceMap?.[b.segmentKey];
          // Fall back to the leg-global row if a per-segment row is missing
          // (shouldn't happen — guarded by product-identity uniformity).
          segMap[paxIdx] = perSegRow || legGlobalOption;
        }
        next[b.segmentKey] = segMap;
      });
      return next;
    };
    if (isReturnLegActive) setReturnTripjackSegmentedBaggageSelections(updater);
    else setTripjackSegmentedBaggageSelections(updater);
  }, [isReturnLegActive, tripjackActiveLegBlocks]);

  /** True when this leg-global option is currently selected for the pax. */
  const isLegBaggageSelectedForPax = useCallback((paxIdx, legGlobalOption) => {
    if (!legGlobalOption) return false;
    const stateMap = isReturnLegActive
      ? returnTripjackSegmentedBaggageSelections
      : tripjackSegmentedBaggageSelections;
    const firstBlock = tripjackActiveLegBlocks[0];
    if (!firstBlock) return false;
    const segSel = stateMap?.[firstBlock.segmentKey]?.[paxIdx];
    if (!segSel) return false;
    return baggageProductIdentitySignature(segSel)
      === baggageProductIdentitySignature(legGlobalOption);
  }, [
    isReturnLegActive,
    returnTripjackSegmentedBaggageSelections,
    tripjackSegmentedBaggageSelections,
    tripjackActiveLegBlocks,
  ]);

  /** Audit-friendly route label for the active leg: "BOM → DXB" or "BOM → DXB via DEL". */
  const journeyBaggageRouteLabel = useMemo(() => {
    const segs = tripjackActiveLegBlocks.map((b) => b.segmentRef).filter(Boolean);
    return formatTripjackJourneyBaggageRouteLabel(segs)
      || formatTripjackJourneyBaggageRouteLabel(visibleFlightSegments);
  }, [tripjackActiveLegBlocks, visibleFlightSegments]);

  // Note: previously these effects wiped `tripjackSegmentedBaggageSelections`
  // whenever the leg flipped to 'journey' mode. With the new leg-global
  // picker (industry-standard), the picker writes the per-segment rows INTO
  // that same map — so wiping it would discard the user's leg choice on
  // every re-render. Leave selections intact; the picker is the only writer.

  /** Itinerary segment cards when blocks have SSR rows (blocks-driven; source via `isTripjack` for flat list gates). */
  const shouldRenderTripjackSegmentedSsr = Array.isArray(tripjackSegmentedBlocks)
    && tripjackSegmentedBlocks.some(
      (block) =>
        (block.baggageOptions?.length || 0) > 0
        || (block.mealOptions?.length || 0) > 0,
    );
  /** Hide global meals list when meals are shown inside segment cards. */
  const shouldUseTripjackSegmentedMeals = Array.isArray(tripjackSegmentedBlocks)
    && tripjackSegmentedBlocks.some(
      (block) => Array.isArray(block.mealOptions) && block.mealOptions.length > 0,
    );
  const shouldUseTripjackSegmentedBaggage = Array.isArray(tripjackSegmentedBlocks)
    && tripjackSegmentedBlocks.some(
      (block) => Array.isArray(block.baggageOptions) && block.baggageOptions.length > 0,
    );

  useEffect(() => {
    if (!isTripjack || !SSR_DEBUG) return;
    const fd = bookingData?.flightData;
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG] available sources', {
      flightDataKeys: Object.keys(fd || {}),
      selectedFlightKeys: Object.keys(fd?.flight || {}),
      reviewDataKeys: Object.keys(reviewPayloadForSegments || {}),
      seatMapsLength: seatmapNormalizedSegments?.length,
      seatMapOptionsLength: seatmapNormalizedSegments?.length,
      visibleFlightSegmentsLength: visibleFlightSegments?.length,
    });
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG] visibleFlightSegments', visibleFlightSegments);
    // eslint-disable-next-line no-console
    console.log('[SSR SEGMENT DEBUG] finalBlocks', tripjackSegmentedBlocks);
  }, [bookingData?.flightData, reviewPayloadForSegments, seatmapNormalizedSegments, visibleFlightSegments, tripjackSegmentedBlocks, isTripjack]);

  const tripjackNestedMealPicks = isReturnLegActive
    ? returnTripjackSegmentedMealSelections
    : tripjackSegmentedMealSelections;
  const setTripjackNestedMealPicks = isReturnLegActive
    ? setReturnTripjackSegmentedMealSelections
    : setTripjackSegmentedMealSelections;
  const tripjackNestedBaggagePicks = isReturnLegActive
    ? returnTripjackSegmentedBaggageSelections
    : tripjackSegmentedBaggageSelections;
  const setTripjackNestedBaggagePicks = isReturnLegActive
    ? setReturnTripjackSegmentedBaggageSelections
    : setTripjackSegmentedBaggageSelections;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 pb-32 md:pb-8">
      <style>{`
        /* Smooth horizontal scrolling for mobile carousels */
        .addon-carousel {
          -webkit-overflow-scrolling: touch;
          scroll-behavior: smooth;
        }
        
        /* Hide scrollbar but keep functionality */
        .addon-carousel::-webkit-scrollbar {
          display: none;
        }
        .addon-carousel {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* Mobile Only Navbar */}
      <div className="md:hidden">
        <Navbar />
      </div>

      {/* Mobile Only Page Header */}
      <div className="md:hidden">
        <BookingPageHeader
          title="Add Extra Services"
          subtitle="Enhance your travel experience (Optional)"
          step={2}
          totalSteps={3}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Desktop: 2-Column Grid | Mobile: Single Column */}
        <div className="md:grid md:grid-cols-[2fr_1fr] md:gap-6 md:items-start">
          {/* LEFT COLUMN: Main Content */}
          <div className="space-y-6">
            {/* Flight Details Card */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-3 md:hidden">
                Flight Details
              </h3>
              <FlightDetailsCard
                flightData={bookingData?.flightData}
                displayFlight={
                  bookingData?.flightData?.flight || bookingData?.flightData
                }
              />
            </div>

            {/* Tripjack RT leg toggle (Onward / Return) */}
            {isTripjackRT && (
              <div className="bg-white rounded-xl shadow-md p-4 sm:p-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-gray-800">
                      Select add-ons for
                    </h3>
                    <p className="text-xs text-gray-500">
                      Choose baggage/meals/extras per leg (seats are per segment)
                    </p>
                  </div>
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => setActiveLeg('outbound')}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                        activeLeg === 'outbound' ? 'bg-white text-[#1B375B] shadow' : 'text-gray-600'
                      }`}
                      data-testid="tripjack-leg-outbound-btn"
                    >
                      Onward
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveLeg('return')}
                      className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                        activeLeg === 'return' ? 'bg-white text-[#1B375B] shadow' : 'text-gray-600'
                      }`}
                      data-testid="tripjack-leg-return-btn"
                    >
                      Return
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Riya catalogue loading / error banner */}
            {bookingData?.flightData?.source === 'riya' && riyaSsrLoading && (
              <div
                data-testid="riya-ssr-loading-banner"
                className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-900 flex items-center gap-2"
              >
                <span className="inline-block w-4 h-4 border-2 border-blue-300 border-t-blue-700 rounded-full animate-spin" />
                Loading available add-ons from Riya…
              </div>
            )}
            {bookingData?.flightData?.source === 'riya' && !riyaSsrLoading && riyaSsrError && (
              <div
                data-testid="riya-ssr-error-banner"
                className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900"
              >
                <p className="font-semibold mb-0.5">Couldn't load add-ons</p>
                <p>{riyaSsrError}</p>
              </div>
            )}

        {/* Baggage Protection */}
        {ssrOptions.baggageProtection && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">
                  Baggage Protection
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Protect your baggage against loss or damage during the journey
                </p>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="baggage-protection"
                      checked={baggageProtection}
                      onChange={(e) => setBaggageProtection(e.target.checked)}
                      className="w-5 h-5 text-[#659A9B] rounded focus:ring-2 focus:ring-[#659A9B]"
                    />
                    <label
                      htmlFor="baggage-protection"
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      Add baggage protection for all passengers
                    </label>
                  </div>

                  <span className="text-lg font-bold text-[#1B375B]">
                    +₹{ssrOptions.baggageProtection.price}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sector-wise baggage + meals (itinerary segment blocks) */}
        {shouldRenderTripjackSegmentedSsr && (
          <div className="space-y-4">
            {tripjackBaggageMode === 'journey' && journeyBaggageDisplayOptions.length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-[#659A9B]/20 overflow-hidden mb-4">
                <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-4 py-3">
                  <h3
                    className="text-white font-bold text-sm tracking-tight"
                    data-testid={`leg-baggage-card-title-${isReturnLegActive ? 'return' : 'onward'}`}
                  >
                    {`Baggage for ${journeyBaggageRouteLabel || (isReturnLegActive ? 'Return Leg' : 'Onward Leg')}`}
                  </h3>
                  <p className="text-white/90 text-xs mt-0.5 font-medium leading-snug">
                    {isTripjackRT
                      ? (isReturnLegActive
                          ? 'Applies to the whole return leg — one selection covers every connection.'
                          : 'Applies to the whole onward leg — one selection covers every connection.')
                      : 'Applies to the whole journey — one selection covers every connection.'}
                  </p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="space-y-4">
                    {passengers.map((passenger, idx) => (
                      <div key={`journey-bag-pax-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                        <p className="text-sm font-semibold text-gray-800 mb-2">
                          {passenger.title} {passenger.firstName} {passenger.lastName}
                        </p>
                        <div className="md:hidden overflow-x-auto pb-2 -mx-1 px-1 addon-carousel">
                          <div className="flex gap-3 snap-x snap-mandatory" style={{ scrollSnapType: 'x mandatory' }}>
                            {journeyBaggageDisplayOptions.map((bagOption, bagIdx) => {
                              const isPicked = isLegBaggageSelectedForPax(idx, bagOption);
                              const legAmt = Number(bagOption._legTotalAmount ?? bagOption.amount) || 0;
                              return (
                                <button
                                  key={`journey-bag-m-${idx}-${String(bagOption.key)}-${String(bagOption.code)}-${bagIdx}`}
                                  type="button"
                                  data-testid={`leg-baggage-mobile-${isReturnLegActive ? 'return' : 'onward'}-pax${idx}-${bagIdx}`}
                                  onClick={() => setLegBaggageForPassenger(idx, isPicked ? null : bagOption)}
                                  className={`
                                    flex-shrink-0 w-36 min-w-[9rem] p-3 rounded-lg border-2 text-center transition-all snap-start
                                    ${isPicked
                                      ? 'border-[#659A9B] bg-[#659A9B]/10'
                                      : 'border-gray-200 hover:border-[#659A9B]/60 bg-white'}
                                  `}
                                >
                                  <div className="font-bold text-gray-800 text-xs leading-tight">{formatSsrDisplayDesc(bagOption.desc)}</div>
                                  {ssrAddonAmountIsIncluded(legAmt) ? (
                                    <span className="text-xs font-medium text-green-600 mt-1 block">Included</span>
                                  ) : (
                                    <div className="text-xs font-semibold text-[#1B375B] mt-1">
                                      +₹{legAmt.toLocaleString()} <span className="font-normal text-[10px] text-gray-500">/ pax</span>
                                    </div>
                                  )}
                                  {isPicked && (
                                    <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-1" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="hidden md:grid grid-cols-4 gap-3">
                          {journeyBaggageDisplayOptions.map((bagOption, bagIdx) => {
                            const isPicked = isLegBaggageSelectedForPax(idx, bagOption);
                            const legAmt = Number(bagOption._legTotalAmount ?? bagOption.amount) || 0;
                            return (
                              <button
                                key={`journey-bag-d-${idx}-${String(bagOption.key)}-${String(bagOption.code)}-${bagIdx}`}
                                type="button"
                                data-testid={`leg-baggage-${isReturnLegActive ? 'return' : 'onward'}-pax${idx}-${bagIdx}`}
                                onClick={() => setLegBaggageForPassenger(idx, isPicked ? null : bagOption)}
                                className={`
                                  p-3 rounded-lg border-2 text-center transition-all
                                  ${isPicked
                                    ? 'border-[#659A9B] bg-[#659A9B]/10'
                                    : 'border-gray-200 hover:border-[#659A9B]/60 bg-white'}
                                `}
                              >
                                <div className="font-bold text-gray-800 text-sm leading-tight">{formatSsrDisplayDesc(bagOption.desc)}</div>
                                {ssrAddonAmountIsIncluded(legAmt) ? (
                                  <span className="text-xs font-medium text-green-600 mt-1 block">Included</span>
                                ) : (
                                  <div className="text-xs font-semibold text-[#1B375B] mt-1">
                                    +₹{legAmt.toLocaleString()} <span className="font-normal text-[10px] text-gray-500">/ pax</span>
                                  </div>
                                )}
                                {isPicked && (
                                  <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-1" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {tripjackSegmentedBlocks.map((segBlock, segIdx) => {
              const sectorLine = tripjackSegmentCardLabelFromSi(segBlock.segmentRef) || segBlock.label;
              const hasBag = (segBlock.baggageOptions?.length || 0) > 0;
              const hasMeals = (segBlock.mealOptions?.length || 0) > 0;
              const showBagSector = tripjackBaggageMode === 'segment' && hasBag;
              return (
                <div
                  key={segBlock.segmentKey || `tj-seg-${segBlock.segmentId}`}
                  className="bg-white rounded-xl shadow-md border border-[#659A9B]/20 overflow-hidden mb-4 last:mb-0"
                >
                  <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-4 py-3">
                    <h3 className="text-white font-bold text-sm tracking-tight">
                      {`Segment ${segIdx + 1}`}
                    </h3>
                    {sectorLine ? (
                      <p className="text-white/90 text-xs mt-0.5 font-medium leading-snug">
                        {sectorLine}
                      </p>
                    ) : null}
                  </div>

                  {/* Audit §6.1 — banner when SSR rows have no per-segment linkage
                      and were collapsed onto the first segment block. */}
                  {(segBlock._wholeJourneyBag || segBlock._wholeJourneyMeal) ? (
                    <div
                      className="border-b border-amber-200 bg-amber-50/80 px-4 py-2"
                      data-testid={`ssr-whole-journey-banner-${segBlock.segmentKey || segIdx}`}
                    >
                      <p className="text-xs text-amber-900 leading-snug">
                        <span className="font-semibold">Note:</span>{' '}
                        {segBlock._wholeJourneyBag && segBlock._wholeJourneyMeal
                          ? 'These baggage and meal add-ons apply to your whole journey, not just this segment.'
                          : segBlock._wholeJourneyBag
                          ? 'These baggage add-ons apply to your whole journey, not just this segment.'
                          : 'These meal add-ons apply to your whole journey, not just this segment.'}
                      </p>
                    </div>
                  ) : null}

                  <div className="p-4 space-y-5">
                    {tripjackBaggageMode === 'segment' && (
                      hasBag ? (
                      <div className="space-y-3">
                        <div className="border-b border-gray-100 pb-2">
                          <h4 className="text-sm font-semibold text-[#1B375B] flex items-center gap-2">
                            <Luggage className="w-4 h-4 text-[#659A9B] shrink-0" />
                            Extra Baggage
                          </h4>
                        </div>
                        <div className="space-y-4">
                          {passengers.map((passenger, idx) => (
                            <div key={`bag-${segBlock.segmentId}-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                              <p className="text-sm font-semibold text-gray-800 mb-2">
                                {passenger.title} {passenger.firstName} {passenger.lastName}
                              </p>
                              <div className="md:hidden overflow-x-auto pb-2 -mx-1 px-1 addon-carousel">
                                <div className="flex gap-3 snap-x snap-mandatory" style={{ scrollSnapType: 'x mandatory' }}>
                                  {segBlock.baggageOptions.map((bagOption, bagIdx) => (
                                    <button
                                      key={`${segBlock.segmentId}-${String(bagOption.key)}-${String(bagOption.code)}-${bagIdx}`}
                                      type="button"
                                      onClick={() => {
                                        setTripjackNestedBaggagePicks((prev) => ({
                                          ...prev,
                                          [segBlock.segmentKey]: {
                                            ...(prev?.[segBlock.segmentKey] || {}),
                                            [idx]: isSameSsrRow(
                                              prev?.[segBlock.segmentKey]?.[idx],
                                              bagOption,
                                            )
                                              ? null
                                              : bagOption,
                                          },
                                        }));
                                      }}
                                      className={`
                                        flex-shrink-0 w-36 min-w-[9rem] p-3 rounded-lg border-2 text-center transition-all snap-start
                                        ${isSameSsrRow(tripjackNestedBaggagePicks?.[segBlock.segmentKey]?.[idx], bagOption)
                                          ? 'border-[#659A9B] bg-[#659A9B]/10'
                                          : 'border-gray-200 hover:border-[#659A9B]/60 bg-white'}
                                      `}
                                    >
                                      <div className="font-bold text-gray-800 text-xs leading-tight">{formatSsrDisplayDesc(bagOption.desc)}</div>
                                      {ssrAddonAmountIsIncluded(bagOption.amount) ? (
                                        <span className="text-xs font-medium text-green-600 mt-1 block">Included</span>
                                      ) : (
                                        <div className="text-xs font-semibold text-[#1B375B] mt-1">
                                          +₹{(Number(bagOption.amount) || 0).toLocaleString()}
                                        </div>
                                      )}
                                      {isSameSsrRow(tripjackNestedBaggagePicks?.[segBlock.segmentKey]?.[idx], bagOption) && (
                                        <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-1" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="hidden md:grid grid-cols-4 gap-3">
                                {segBlock.baggageOptions.map((bagOption, bagIdx) => (
                                  <button
                                    key={`${segBlock.segmentId}-d-${String(bagOption.key)}-${String(bagOption.code)}-${bagIdx}`}
                                    type="button"
                                    onClick={() => {
                                      setTripjackNestedBaggagePicks((prev) => ({
                                        ...prev,
                                        [segBlock.segmentKey]: {
                                          ...(prev?.[segBlock.segmentKey] || {}),
                                          [idx]: isSameSsrRow(
                                            prev?.[segBlock.segmentKey]?.[idx],
                                            bagOption,
                                          )
                                            ? null
                                            : bagOption,
                                        },
                                      }));
                                    }}
                                    className={`
                                      p-3 rounded-lg border-2 text-center transition-all
                                      ${isSameSsrRow(tripjackNestedBaggagePicks?.[segBlock.segmentKey]?.[idx], bagOption)
                                        ? 'border-[#659A9B] bg-[#659A9B]/10'
                                        : 'border-gray-200 hover:border-[#659A9B]/60 bg-white'}
                                    `}
                                  >
                                    <div className="font-bold text-gray-800 text-sm leading-tight">{formatSsrDisplayDesc(bagOption.desc)}</div>
                                    {ssrAddonAmountIsIncluded(bagOption.amount) ? (
                                      <span className="text-xs font-medium text-green-600 mt-1 block">Included</span>
                                    ) : (
                                      <div className="text-xs font-semibold text-[#1B375B] mt-1">
                                        +₹{(Number(bagOption.amount) || 0).toLocaleString()}
                                      </div>
                                    )}
                                    {isSameSsrRow(tripjackNestedBaggagePicks?.[segBlock.segmentKey]?.[idx], bagOption) && (
                                      <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-1" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No baggage available for this sector.</p>
                    )
                    )}

                    {hasMeals ? (
                      <div className={`space-y-3 ${showBagSector ? 'border-t border-gray-100 pt-5' : ''}`}>
                        <div className="border-b border-gray-100 pb-2">
                          <h4 className="text-sm font-semibold text-[#1B375B] flex items-center gap-2">
                            <Utensils className="w-4 h-4 text-[#659A9B] shrink-0" />
                            In-flight Meals
                          </h4>
                        </div>
                        <div className="space-y-4">
                          {passengers.map((passenger, idx) => (
                            <div key={`meal-${segBlock.segmentId}-${idx}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                              <p className="text-sm font-semibold text-gray-800 mb-2">
                                {passenger.title} {passenger.firstName} {passenger.lastName}
                              </p>
                              <div className="md:hidden overflow-x-auto pb-2 -mx-1 px-1 addon-carousel">
                                <div className="flex gap-3 snap-x snap-mandatory" style={{ scrollSnapType: 'x mandatory' }}>
                                  {segBlock.mealOptions.map((mealOption, mealIdx) => (
                                    <button
                                      key={`${segBlock.segmentId}-${String(mealOption.key)}-${String(mealOption.code)}-${mealIdx}`}
                                      type="button"
                                      onClick={() => {
                                        setTripjackNestedMealPicks((prev) => ({
                                          ...prev,
                                          [segBlock.segmentKey]: {
                                            ...(prev?.[segBlock.segmentKey] || {}),
                                            [idx]: isSameSsrRow(
                                              prev?.[segBlock.segmentKey]?.[idx],
                                              mealOption,
                                            )
                                              ? null
                                              : mealOption,
                                          },
                                        }));
                                      }}
                                      className={`
                                        flex-shrink-0 w-36 min-w-[9rem] p-3 rounded-lg border-2 text-center transition-all snap-start
                                        ${isSameSsrRow(tripjackNestedMealPicks?.[segBlock.segmentKey]?.[idx], mealOption)
                                          ? 'border-[#659A9B] bg-[#659A9B]/10'
                                          : 'border-gray-200 hover:border-[#659A9B]/60 bg-white'}
                                      `}
                                    >
                                      <div className="font-bold text-gray-800 text-xs leading-tight">{formatSsrDisplayDesc(mealOption.desc)}</div>
                                      {ssrAddonAmountIsIncluded(mealOption.amount) ? (
                                        <span className="text-xs font-medium text-green-600 mt-1 block">Included</span>
                                      ) : (
                                        <div className="text-xs font-semibold text-[#1B375B] mt-1">
                                          +₹{(Number(mealOption.amount) || 0).toLocaleString()}
                                        </div>
                                      )}
                                      {isSameSsrRow(tripjackNestedMealPicks?.[segBlock.segmentKey]?.[idx], mealOption) && (
                                        <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-1" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="hidden md:grid md:grid-cols-3 xl:grid-cols-4 gap-3">
                                {segBlock.mealOptions.map((mealOption, mealIdx) => (
                                  <button
                                    key={`${segBlock.segmentId}-md-${String(mealOption.key)}-${String(mealOption.code)}-${mealIdx}`}
                                    type="button"
                                    onClick={() => {
                                      setTripjackNestedMealPicks((prev) => ({
                                        ...prev,
                                        [segBlock.segmentKey]: {
                                          ...(prev?.[segBlock.segmentKey] || {}),
                                          [idx]: isSameSsrRow(
                                            prev?.[segBlock.segmentKey]?.[idx],
                                            mealOption,
                                          )
                                            ? null
                                            : mealOption,
                                        },
                                      }));
                                    }}
                                    className={`
                                      p-3 rounded-lg border-2 text-center transition-all
                                      ${isSameSsrRow(tripjackNestedMealPicks?.[segBlock.segmentKey]?.[idx], mealOption)
                                        ? 'border-[#659A9B] bg-[#659A9B]/10'
                                        : 'border-gray-200 hover:border-[#659A9B]/60 bg-white'}
                                    `}
                                  >
                                    <div className="font-bold text-gray-800 text-sm leading-tight">{formatSsrDisplayDesc(mealOption.desc)}</div>
                                    {ssrAddonAmountIsIncluded(mealOption.amount) ? (
                                      <span className="text-xs font-medium text-green-600 mt-1 block">Included</span>
                                    ) : (
                                      <div className="text-xs font-semibold text-[#1B375B] mt-1">
                                        +₹{(Number(mealOption.amount) || 0).toLocaleString()}
                                      </div>
                                    )}
                                    {isSameSsrRow(tripjackNestedMealPicks?.[segBlock.segmentKey]?.[idx], mealOption) && (
                                      <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-1" />
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className={`text-xs text-gray-500 ${showBagSector ? 'border-t border-gray-100 pt-5' : ''}`}>
                        No meals available for this sector.
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Extra Baggage — global list only when not using Tripjack segment cards */}
        {!shouldUseTripjackSegmentedBaggage && currentBaggageOptions.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Luggage className="w-6 h-6 text-green-600" />
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">
                  Extra Baggage
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Add extra baggage allowance for your passengers
                </p>
                <p className="text-xs text-gray-500 mt-1 md:hidden flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7M5 5l7 7-7 7"
                    />
                  </svg>
                  Swipe to see all options
                </p>
              </div>
            </div>

            {/* Baggage options for each passenger */}
            <div className="space-y-4 mt-6">
              {passengers.map((passenger, idx) => (
                <div key={idx} className="border-t pt-4">
                  <p className="font-medium text-gray-800 mb-3">
                    {passenger.title} {passenger.firstName} {passenger.lastName}
                  </p>

                  {/* Mobile: Horizontal Scroll Carousel */}
                  <div className="md:hidden overflow-x-auto pb-2 -mx-2 px-2 addon-carousel">
                    <div
                      className="flex gap-3 snap-x snap-mandatory"
                      style={{ scrollSnapType: "x mandatory" }}
                    >
                      {currentBaggageOptions.map((bagOption, bagIdx) => (
                        <button
                          key={`${String(bagOption.key)}-${String(bagOption.code)}-${bagIdx}`}
                          onClick={() => {
                            setCurrentSelectedBaggage((prev) => ({
                              ...prev,
                              [idx]:
                                isSameSsrRow(prev[idx], bagOption)
                                  ? null
                                  : bagOption,
                            }));
                          }}
                          className={`
                            flex-shrink-0 w-28 p-2.5 rounded-lg border-2 transition-all text-center snap-start
                            ${
                              isSameSsrRow(currentSelectedBaggage[idx], bagOption)
                                ? "border-[#659A9B] bg-[#659A9B]/10"
                                : "border-gray-200 hover:border-gray-300"
                            }
                          `}
                        >
                          <div className="font-bold text-gray-800 text-xs">
                            {bagOption.desc}
                          </div>
                          <div className="text-xs text-[#1B375B] font-medium mt-0.5">
                            +₹{(bagOption.amount || 0).toLocaleString()}
                          </div>
                          {isSameSsrRow(currentSelectedBaggage[idx], bagOption) && (
                            <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-0.5" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Desktop: Grid Layout */}
                  <div className="hidden md:grid grid-cols-4 gap-3">
                    {currentBaggageOptions.map((bagOption, bagIdx) => (
                      <button
                        key={`${String(bagOption.key)}-${String(bagOption.code)}-${bagIdx}`}
                        onClick={() => {
                          setCurrentSelectedBaggage((prev) => ({
                            ...prev,
                            [idx]:
                              isSameSsrRow(prev[idx], bagOption)
                                ? null
                                : bagOption,
                          }));
                        }}
                        className={`
                          p-2.5 rounded-lg border-2 transition-all text-center
                          ${
                            isSameSsrRow(currentSelectedBaggage[idx], bagOption)
                              ? "border-[#659A9B] bg-[#659A9B]/10"
                              : "border-gray-200 hover:border-gray-300"
                          }
                        `}
                      >
                        <div className="font-bold text-gray-800 text-sm">
                          {bagOption.desc}
                        </div>
                        <div className="text-xs text-[#1B375B] font-medium mt-0.5">
                          +₹{(bagOption.amount || 0).toLocaleString()}
                        </div>
                        {isSameSsrRow(currentSelectedBaggage[idx], bagOption) && (
                          <Check className="w-3.5 h-3.5 text-[#659A9B] mx-auto mt-0.5" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Meals Selection — global list only when not using Tripjack segment cards */}
        {!shouldUseTripjackSegmentedMeals && currentMealOptions.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Utensils className="w-6 h-6 text-orange-600" />
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">
                  In-Flight Meals
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Pre-book your preferred meal for a delightful journey
                </p>
                <p className="text-xs text-gray-500 mt-1 md:hidden flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7M5 5l7 7-7 7"
                    />
                  </svg>
                  Swipe to see all options
                </p>
              </div>
            </div>

            {/* Meal options for each passenger */}
            <div className="space-y-4 mt-6">
              {passengers.map((passenger, idx) => (
                <div key={idx} className="border-t pt-4">
                  <p className="font-medium text-gray-800 mb-3">
                    {passenger.title} {passenger.firstName} {passenger.lastName}
                  </p>

                  {/* Mobile: Horizontal Scroll Carousel */}
                  <div className="md:hidden overflow-x-auto pb-2 -mx-2 px-2 addon-carousel">
                    <div
                      className="flex gap-3 snap-x snap-mandatory"
                      style={{ scrollSnapType: "x mandatory" }}
                    >
                      {currentMealOptions.map((mealOption, mealIdx) => (
                        <button
                          key={`${String(mealOption.key)}-${String(mealOption.code)}-${mealIdx}`}
                          onClick={() => {
                            setCurrentSelectedMeals((prev) => ({
                              ...prev,
                              [idx]:
                                isSameSsrRow(prev[idx], mealOption)
                                  ? null
                                  : mealOption,
                            }));
                          }}
                          className={`
                            flex-shrink-0 w-36 p-2.5 rounded-lg border-2 transition-all text-center snap-start
                            ${
                              isSameSsrRow(currentSelectedMeals[idx], mealOption)
                                ? "border-orange-500 bg-orange-50"
                                : "border-gray-200 hover:border-gray-300"
                            }
                          `}
                        >
                          <div className="font-bold text-gray-800 text-xs leading-tight">
                            {formatSsrDisplayDesc(mealOption.desc)}
                          </div>
                          <div className="text-xs text-orange-600 font-medium mt-0.5">
                            +₹{(mealOption.amount || 0).toLocaleString()}
                          </div>
                          {isSameSsrRow(currentSelectedMeals[idx], mealOption) && (
                            <Check className="w-3.5 h-3.5 text-orange-600 mx-auto mt-0.5" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Desktop: Grid Layout */}
                  <div className="hidden md:grid grid-cols-3 gap-3">
                    {currentMealOptions.map((mealOption, mealIdx) => (
                      <button
                        key={`${String(mealOption.key)}-${String(mealOption.code)}-${mealIdx}`}
                        onClick={() => {
                          setCurrentSelectedMeals((prev) => ({
                            ...prev,
                            [idx]:
                              isSameSsrRow(prev[idx], mealOption)
                                ? null
                                : mealOption,
                          }));
                        }}
                        className={`
                          p-2.5 rounded-lg border-2 transition-all text-center
                          ${
                            isSameSsrRow(currentSelectedMeals[idx], mealOption)
                              ? "border-orange-500 bg-orange-50"
                              : "border-gray-200 hover:border-gray-300"
                          }
                        `}
                      >
                        <div className="font-bold text-gray-800 text-sm leading-tight">
                          {formatSsrDisplayDesc(mealOption.desc)}
                        </div>
                        <div className="text-xs text-orange-600 font-medium mt-0.5">
                          +₹{(mealOption.amount || 0).toLocaleString()}
                        </div>
                        {isSameSsrRow(currentSelectedMeals[idx], mealOption) && (
                          <Check className="w-3.5 h-3.5 text-orange-600 mx-auto mt-0.5" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extra services (Tripjack EXTRASERVICES → ssrExtraServiceInfos on /book) */}
        {currentExtrasOptions.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">Extra services</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Priority boarding, fast-forward, and other airline add-ons when available
                </p>
              </div>
            </div>
            <div className="space-y-4 mt-6">
              {passengers.map((passenger, idx) => (
                <div key={`extra-${idx}`} className="border-t pt-4">
                  <p className="font-medium text-gray-800 mb-3">
                    {passenger.title} {passenger.firstName} {passenger.lastName}
                  </p>
                  <div className="md:hidden overflow-x-auto pb-2 -mx-2 px-2 addon-carousel">
                    <div className="flex gap-3 snap-x snap-mandatory" style={{ scrollSnapType: 'x mandatory' }}>
                      {currentExtrasOptions.map((opt, oi) => (
                        <button
                          key={`${String(opt.key)}-${String(opt.code)}-${oi}`}
                          type="button"
                          onClick={() => {
                            setCurrentSelectedExtras((prev) => ({
                              ...prev,
                              [idx]: isSameSsrRow(prev[idx], opt) ? null : opt,
                            }));
                          }}
                          className={`flex-shrink-0 w-36 p-2.5 rounded-lg border-2 transition-all text-center snap-start ${
                            isSameSsrRow(currentSelectedExtras[idx], opt)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-bold text-gray-800 text-xs leading-tight">{opt.desc}</div>
                          <div className="text-xs text-teal-700 font-medium mt-0.5">
                            +₹{(opt.amount || 0).toLocaleString()}
                          </div>
                          {isSameSsrRow(currentSelectedExtras[idx], opt) && (
                            <Check className="w-3.5 h-3.5 text-teal-600 mx-auto mt-0.5" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="hidden md:grid grid-cols-3 gap-3">
                    {currentExtrasOptions.map((opt, oi) => (
                      <button
                        key={`${String(opt.key)}-${String(opt.code)}-${oi}`}
                        type="button"
                        onClick={() => {
                          setCurrentSelectedExtras((prev) => ({
                            ...prev,
                            [idx]: isSameSsrRow(prev[idx], opt) ? null : opt,
                          }));
                        }}
                        className={`p-2.5 rounded-lg border-2 transition-all text-center ${
                          isSameSsrRow(currentSelectedExtras[idx], opt)
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-bold text-gray-800 text-sm leading-tight">{opt.desc}</div>
                        <div className="text-xs text-teal-700 font-medium mt-0.5">
                          +₹{(opt.amount || 0).toLocaleString()}
                        </div>
                        {isSameSsrRow(currentSelectedExtras[idx], opt) && (
                          <Check className="w-3.5 h-3.5 text-teal-600 mx-auto mt-0.5" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* G6 · Interactive seat map (Tripjack /seatmap). Renders above the
            legacy flat seat list so users get the airline-diagram UX first;
            falls back silently if /seatmap returns no data. The card
            matches the width of all other ancillary cards (Baggage,
            Meals, etc.) — the inner aircraft uses tight natural seat
            sizes and scrolls horizontally inside the card. */}
        {(bookingData?.priceId
          || (bookingData?.priceIds?.length > 1
            ? bookingData.priceIds.join(',')
            : bookingData?.priceIds?.[0])) && (
          <div className="mb-4 sm:mb-6">
            <InteractiveSeatMap
              priceId={
                Array.isArray(bookingData?.priceIds) && bookingData.priceIds.length > 1
                  ? bookingData.priceIds.join(',')
                  : (bookingData?.priceId || bookingData?.priceIds?.[0])
              }
              tripjackBookingId={
                bookingData?.tripjackBookingId
                || bookingData?.reviewData?.bookingId
                || bookingData?.reviewData?.orderId
                || null
              }
              passengers={passengers}
              segments={visibleFlightSegments}
              onSelectionsChange={setSeatMapSelections}
              onSegmentsNormalized={handleSeatmapSegmentsNormalized}
            />
          </div>
        )}

        {/* Seat Selection */}
        {ssrOptions.seats.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">
                  Quick Seat Options
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Or pick a fast-track seat from the airline-suggested list
                </p>
                <p className="text-xs text-gray-500 mt-1 md:hidden flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 5l7 7-7 7M5 5l7 7-7 7"
                    />
                  </svg>
                  Swipe to see all seats
                </p>
              </div>
            </div>

            {/* Seat options for each passenger */}
            <div className="space-y-4 mt-6">
              {passengers.map((passenger, idx) => (
                <div key={idx} className="border-t pt-4">
                  <p className="font-medium text-gray-800 mb-3">
                    {passenger.title} {passenger.firstName} {passenger.lastName}
                  </p>

                  {/* Mobile: Horizontal Scroll Carousel */}
                  <div className="md:hidden overflow-x-auto pb-2 -mx-2 px-2 addon-carousel">
                    <div
                      className="flex gap-2 snap-x snap-mandatory"
                      style={{ scrollSnapType: "x mandatory" }}
                    >
                      {ssrOptions.seats.map((seatOption, seatIdx) => (
                        <button
                          key={`${String(seatOption.key)}-${String(seatOption.code)}-${seatIdx}`}
                          onClick={() => {
                            setSelectedSeats((prev) => ({
                              ...prev,
                              [idx]:
                                isSameSsrRow(prev[idx], seatOption)
                                  ? null
                                  : seatOption,
                            }));
                          }}
                          className={`
                            flex-shrink-0 w-20 p-3 rounded-lg border-2 transition-all text-center relative snap-start
                            ${
                              isSameSsrRow(selectedSeats[idx], seatOption)
                                ? "border-purple-500 bg-purple-50"
                                : "border-gray-200 hover:border-gray-300"
                            }
                            ${seatOption.isLegroom ? "ring-2 ring-blue-300" : ""}
                          `}
                        >
                          {seatOption.isLegroom && (
                            <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] px-1 rounded-full font-bold">
                              LEG
                            </span>
                          )}
                          <div className="font-bold text-gray-800 text-sm">
                            {seatOption.code}
                          </div>
                          <div className="text-xs text-purple-600 font-medium mt-0.5">
                            +₹{seatOption.amount}
                          </div>
                          {isSameSsrRow(selectedSeats[idx], seatOption) && (
                            <Check className="w-3 h-3 text-purple-600 mx-auto mt-1" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Desktop: Grid Layout */}
                  <div className="hidden md:grid grid-cols-6 gap-2">
                    {ssrOptions.seats.map((seatOption, seatIdx) => (
                      <button
                        key={`${String(seatOption.key)}-${String(seatOption.code)}-${seatIdx}`}
                        onClick={() => {
                          setSelectedSeats((prev) => ({
                            ...prev,
                            [idx]:
                              isSameSsrRow(prev[idx], seatOption)
                                ? null
                                : seatOption,
                          }));
                        }}
                        className={`
                          p-3 rounded-lg border-2 transition-all text-center relative
                          ${
                            isSameSsrRow(selectedSeats[idx], seatOption)
                              ? "border-purple-500 bg-purple-50"
                              : "border-gray-200 hover:border-gray-300"
                          }
                          ${seatOption.isLegroom ? "ring-2 ring-blue-300" : ""}
                        `}
                      >
                        {seatOption.isLegroom && (
                          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[8px] px-1 rounded-full font-bold">
                            LEG
                          </span>
                        )}
                        <div className="font-bold text-gray-800">
                          {seatOption.code}
                        </div>
                        <div className="text-xs text-purple-600 font-medium mt-0.5">
                          +₹{seatOption.amount}
                        </div>
                        {isSameSsrRow(selectedSeats[idx], seatOption) && (
                          <Check className="w-3 h-3 text-purple-600 mx-auto mt-1" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No SSR Available */}
        {ssrOptions.baggage.length === 0 &&
          ssrOptions.meals.length === 0 &&
          ssrOptions.seats.length === 0 &&
          !ssrOptions.baggageProtection &&
          !shouldRenderTripjackSegmentedSsr &&
          (!isTripjackRT || (
            (returnSsrOptions.baggage || []).length === 0
            && (returnSsrOptions.meals || []).length === 0
            && (returnSsrOptions.seats || []).length === 0
          )) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-start gap-2 sm:gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-yellow-800">
                    No Extra Services Available
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    This flight doesn't have additional services available. You
                    can proceed to payment.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Sprint-3 · Riya RT — Return-leg add-on picker. Compact, stacked
            below the outbound catalogue. Selections live in their own state
            and are emitted as `returnSsrSelections` on /book. */}
        {isRiyaRT && (returnSsrOptions.baggage.length > 0 || returnSsrOptions.meals.length > 0) && (
          <div
            className="bg-white border-2 border-[#659A9B]/40 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6"
            data-testid="riya-rt-return-leg-section"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#1B375B] text-base sm:text-lg flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-[#659A9B]" />
                Return-leg Add-ons
              </h3>
              <span className="text-xs text-gray-500">
                {bookingData?.flightData?.return?.from || ''} → {bookingData?.flightData?.return?.to || ''}
              </span>
            </div>

            {/* Return-leg baggage (per-pax dropdowns) */}
            {returnSsrOptions.baggage.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                  <Luggage className="w-4 h-4" /> Extra Baggage (Return)
                </p>
                <div className="space-y-2">
                  {(bookingData?.passengersRaw || []).map((pax, idx) => (
                    <div
                      key={`rt-bag-${idx}`}
                      className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2"
                      data-testid={`riya-rt-bag-pax-${idx}`}
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {(pax.firstName || pax.fN || `Pax ${idx + 1}`)} {pax.lastName || pax.lN || ''}
                      </span>
                      <select
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                        value={returnSelectedBaggage[idx]?.key || ''}
                        onChange={(e) => {
                          const sel = returnSsrOptions.baggage.find(b => b.key === e.target.value);
                          setReturnSelectedBaggage((prev) => ({ ...prev, [idx]: sel || null }));
                        }}
                        data-testid={`riya-rt-bag-select-${idx}`}
                      >
                        <option value="">No extra baggage</option>
                        {returnSsrOptions.baggage.map((b) => (
                          <option key={b.key} value={b.key}>
                            {b.desc} (+₹{b.amount})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Return-leg meals (per-pax dropdowns) */}
            {returnSsrOptions.meals.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                  <Utensils className="w-4 h-4" /> Meals (Return)
                </p>
                <div className="space-y-2">
                  {(bookingData?.passengersRaw || []).map((pax, idx) => (
                    <div
                      key={`rt-meal-${idx}`}
                      className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2"
                      data-testid={`riya-rt-meal-pax-${idx}`}
                    >
                      <span className="text-sm text-gray-700 truncate">
                        {(pax.firstName || pax.fN || `Pax ${idx + 1}`)} {pax.lastName || pax.lN || ''}
                      </span>
                      <select
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                        value={returnSelectedMeals[idx]?.key || ''}
                        onChange={(e) => {
                          const sel = returnSsrOptions.meals.find(m => m.key === e.target.value);
                          setReturnSelectedMeals((prev) => ({ ...prev, [idx]: sel || null }));
                        }}
                        data-testid={`riya-rt-meal-select-${idx}`}
                      >
                        <option value="">No meal</option>
                        {returnSsrOptions.meals.map((m) => (
                          <option key={m.key} value={m.key}>
                            {m.desc} (+₹{m.amount})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
          </div>

          {/* RIGHT COLUMN: Sticky Fare Summary (Desktop Only) */}
          <div className="hidden md:block">
            <div className="sticky top-6 max-w-sm mx-auto">
              <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg border-2 border-[#659A9B] overflow-hidden">
                <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4">
                  <h3 className="text-lg font-bold text-white">Fare Summary</h3>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-3">
                    {ancillaryFareDisplay &&
                    ancillaryFareDisplay.baseFare + ancillaryFareDisplay.taxes > 0 ? (
                      <>
                        {ancillaryFareDisplay.paxLine && (
                          <div className="text-xs text-gray-500 pb-2 border-b border-gray-100">
                            <span className="font-medium text-gray-700">Travelers:</span>{" "}
                            {ancillaryFareDisplay.paxLine}
                          </div>
                        )}
                        <div className="flex justify-between text-sm py-2">
                          <span className="text-gray-600">Base Fare</span>
                          <span className="font-semibold text-gray-800">
                            ₹{ancillaryFareDisplay.baseFare.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm py-2">
                          <span className="text-gray-600">Taxes & Fees</span>
                          <span className="font-semibold text-gray-800">
                            ₹{ancillaryFareDisplay.taxes.toLocaleString()}
                          </span>
                        </div>
                        {ancillaryFareDisplay.serviceCharge > 0 && (
                          <div className="flex justify-between text-sm py-2" data-testid="ancillary-service-charge-row">
                            <span className="text-gray-600">Service Charge</span>
                            <span className="font-semibold text-gray-800">
                              ₹{ancillaryFareDisplay.serviceCharge.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {ancillaryFareDisplay.muSubLine && ancillaryFareDisplay.serviceCharge > 0 && (
                          <p className="text-[11px] text-gray-500 -mt-1 mb-1" data-testid="ancillary-service-charge-breakdown">
                            {ancillaryFareDisplay.muSubLine}
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="flex justify-between text-sm py-2">
                        <span className="text-gray-600">Flight fare (incl. taxes and service)</span>
                        <span className="font-semibold text-gray-800">
                          ₹{Number(bookingData?.totalAmount || 0).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* Divider before add-ons */}
                    {(baggageProtection ||
                      tripjackNestedBaggageHasAnySelection(tripjackSegmentedBaggageSelections) ||
                      (isTripjackRT && tripjackNestedBaggageHasAnySelection(returnTripjackSegmentedBaggageSelections)) ||
                      Object.keys(selectedBaggage).length > 0 ||
                      tripjackNestedMealsHasAnySelection(tripjackSegmentedMealSelections) ||
                      (isTripjackRT && tripjackNestedMealsHasAnySelection(returnTripjackSegmentedMealSelections)) ||
                      Object.keys(selectedMeals).length > 0 ||
                      Object.keys(selectedSeats).length > 0 ||
                      Object.keys(selectedExtras).length > 0 ||
                      Object.values(seatMapSelections || {}).some(
                        (segPicks) =>
                          segPicks &&
                          Object.values(segPicks).some((s) => s?.code)
                      )) && (
                      <div className="border-t border-gray-300 my-3"></div>
                    )}

                    {/* Baggage Protection */}
                    {baggageProtection && ssrOptions.baggageProtection && (
                      <div className="flex justify-between text-sm py-2">
                        <span className="text-gray-600">Baggage Protection</span>
                        <span className="font-semibold text-[#659A9B]">
                          +₹{ssrOptions.baggageProtection.price}
                        </span>
                      </div>
                    )}

                    {/* Extra Baggage — Tripjack segment cards use nested state; flat list otherwise */}
                    {outboundBaggageSidebarRows.map(({ key, segmentLabel, passenger, baggage }) => (
                      <div key={key} className="flex justify-between text-sm py-2">
                        <span className="text-gray-600 text-xs">
                          Extra Baggage ({passenger?.firstName} - {segmentLabel} - {baggage.desc})
                        </span>
                        <span className="font-semibold text-[#659A9B]">
                          +₹{(baggage.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {!tripjackNestedBaggageHasAnySelection(tripjackSegmentedBaggageSelections)
                      && Object.entries(selectedBaggage).map(
                      ([passengerIdx, baggage]) => {
                        if (!baggage) return null;
                        const passenger = passengers[passengerIdx];
                        return (
                          <div
                            key={`bag-${passengerIdx}`}
                            className="flex justify-between text-sm py-2"
                          >
                            <span className="text-gray-600 text-xs">
                              Extra Baggage ({passenger?.firstName} -{" "}
                              {baggage.desc})
                            </span>
                            <span className="font-semibold text-[#659A9B]">
                              +₹{(baggage.amount || 0).toLocaleString()}
                            </span>
                          </div>
                        );
                      }
                    )}
                    {returnBaggageSidebarRows.map(({ key, segmentLabel, passenger, baggage }) => (
                      <div key={`rt-bag-${key}`} className="flex justify-between text-sm py-2">
                        <span className="text-gray-600 text-xs">
                          Extra Baggage Return ({passenger?.firstName} - {segmentLabel} - {baggage.desc})
                        </span>
                        <span className="font-semibold text-[#659A9B]">
                          +₹{(baggage.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {isTripjackRT && !tripjackNestedBaggageHasAnySelection(returnTripjackSegmentedBaggageSelections)
                      && Object.entries(returnSelectedBaggage).map(([passengerIdx, baggage]) => {
                      if (!baggage) return null;
                      const passenger = passengers[passengerIdx];
                      return (
                        <div
                          key={`bag-rt-flat-${passengerIdx}`}
                          className="flex justify-between text-sm py-2"
                        >
                          <span className="text-gray-600 text-xs">
                            Extra Baggage Return ({passenger?.firstName} - {baggage.desc})
                          </span>
                          <span className="font-semibold text-[#659A9B]">
                            +₹{(baggage.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}

                    {/* Meals — Tripjack segment cards use nested state; flat list otherwise */}
                    {outboundMealSidebarRows.map(({ key, segmentLabel, passenger, meal }) => (
                      <div key={key} className="flex justify-between text-sm py-2">
                        <span className="text-gray-600 text-xs">
                          Meal ({passenger?.firstName} - {segmentLabel} - {formatSsrDisplayDesc(meal.desc)})
                        </span>
                        <span className="font-semibold text-[#659A9B]">
                          +₹{(meal.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {!tripjackNestedMealsHasAnySelection(tripjackSegmentedMealSelections)
                      && Object.entries(selectedMeals).map(([passengerIdx, meal]) => {
                      if (!meal) return null;
                      const passenger = passengers[passengerIdx];
                      return (
                        <div
                          key={`meal-${passengerIdx}`}
                          className="flex justify-between text-sm py-2"
                        >
                          <span className="text-gray-600 text-xs">
                            Meal ({passenger?.firstName} - {meal.desc})
                          </span>
                          <span className="font-semibold text-[#659A9B]">
                            +₹{(meal.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                    {returnMealSidebarRows.map(({ key, segmentLabel, passenger, meal }) => (
                      <div key={`rt-meal-${key}`} className="flex justify-between text-sm py-2">
                        <span className="text-gray-600 text-xs">
                          Meal Return ({passenger?.firstName} - {segmentLabel} - {formatSsrDisplayDesc(meal.desc)})
                        </span>
                        <span className="font-semibold text-[#659A9B]">
                          +₹{(meal.amount || 0).toLocaleString()}
                        </span>
                      </div>
                    ))}
                    {isTripjackRT && !tripjackNestedMealsHasAnySelection(returnTripjackSegmentedMealSelections)
                      && Object.entries(returnSelectedMeals).map(([passengerIdx, meal]) => {
                      if (!meal) return null;
                      const passenger = passengers[passengerIdx];
                      return (
                        <div
                          key={`meal-rt-flat-${passengerIdx}`}
                          className="flex justify-between text-sm py-2"
                        >
                          <span className="text-gray-600 text-xs">
                            Meal Return ({passenger?.firstName} - {meal.desc})
                          </span>
                          <span className="font-semibold text-[#659A9B]">
                            +₹{(meal.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}

                    {Object.entries(selectedExtras).map(([passengerIdx, ex]) => {
                      if (!ex) return null;
                      const passenger = passengers[passengerIdx];
                      return (
                        <div
                          key={`extra-${passengerIdx}`}
                          className="flex justify-between text-sm py-2"
                        >
                          <span className="text-gray-600 text-xs">
                            Extra ({passenger?.firstName} - {ex.desc})
                          </span>
                          <span className="font-semibold text-[#659A9B]">
                            +₹{(ex.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}

                    {/* Seats — legacy flat picker */}
                    {Object.entries(selectedSeats).map(([passengerIdx, seat]) => {
                      if (!seat) return null;
                      const passenger = passengers[passengerIdx];
                      return (
                        <div
                          key={`seat-${passengerIdx}`}
                          className="flex justify-between text-sm py-2"
                        >
                          <span className="text-gray-600 text-xs">
                            Seat ({passenger?.firstName} - {seat.desc})
                          </span>
                          <span className="font-semibold text-[#659A9B]">
                            +₹{(seat.amount || 0).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}

                    {/* G6 · Interactive seat-map picks (per segment per pax) */}
                    {Object.entries(seatMapSelections || {}).flatMap(
                      ([segKey, paxPicks]) =>
                        Object.entries(paxPicks || {}).map(([paxIdx, seat]) => {
                          if (!seat || !seat.code) return null;
                          const passenger = passengers[paxIdx];
                          return (
                            <div
                              key={`smap-${segKey}-${paxIdx}`}
                              className="flex justify-between text-sm py-2"
                              data-testid={`addon-line-seat-${segKey}-${paxIdx}`}
                            >
                              <span className="text-gray-600 text-xs">
                                Seat {seat.code}
                                {passenger?.firstName ? ` (${passenger.firstName})` : ''}
                                {seat.segmentLabel ? ` · ${seat.segmentLabel}` : ''}
                              </span>
                              <span className="font-semibold text-[#659A9B]">
                                {Number(seat.price || 0) > 0
                                  ? `+₹${Number(seat.price).toLocaleString()}`
                                  : 'Free'}
                              </span>
                            </div>
                          );
                        })
                    )}

                    {/* Total */}
                    <div className="border-t-2 border-[#659A9B] pt-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800 text-base">
                          {commissionFare.showAmountToPayLabel ? 'Amount to Pay' : 'Total Amount'}
                        </span>
                        <span className="text-2xl font-black text-[#1B375B]">
                          ₹{ancillaryPayTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <FareCommissionRows commissionFare={commissionFare} />
                  </div>
                </div>

                {/* Desktop Only Action Buttons */}
                <div className="mt-6 px-6 pb-6 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="ancillary-skip-btn"
                    onClick={handleSkip}
                    className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                  >
                    <span className="text-xs">Skip</span>
                  </button>
                  <button
                    type="button"
                    data-testid="ancillary-continue-btn"
                    onClick={handleContinue}
                    className="px-4 py-2.5 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <span className="text-xs">Continue</span>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Only Fixed Bottom Bar */}
      {/* Compact Sticky Bottom Menu */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] py-2 px-4">
      <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            {/* Compact Total Display */}
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-gray-500">Total:</span>
              <span className="text-xl md:text-2xl font-bold text-[#1B375B]" data-testid="ancillary-total-fare">
                ₹{calculateTotal().toLocaleString()}
              </span>
            </div>

            {/* Compact Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="ancillary-skip-btn-mobile"
                onClick={handleSkip}
                className="px-4 py-2 md:px-5 md:py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
              >
                Skip
              </button>

              <button
                type="button"
                data-testid="ancillary-continue-btn-mobile"
                onClick={handleContinue}
                className="px-5 py-2 md:px-6 md:py-2.5 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center gap-1.5 shadow-md text-sm"
              >
                Continue
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
};

export default AncillaryServices;
